
import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_PROJECT,
  GET_CLIENTS,
  GET_CATALOGS,
  GET_RESOURCES,
  UPDATE_PROJECT,
  UPDATE_PROJECT_DEVELOPERS,
  ADD_TASK,
  UPDATE_TASK,
  DELETE_TASK,
  ADD_TASK_COMMENT,
  DELETE_TASK_COMMENT,
  ADD_NOTE,
  UPDATE_NOTE,
  DELETE_NOTE,
  ADD_ASSET,
  DELETE_ASSET,
  ADD_PHASE,
  UPDATE_PHASE,
  DELETE_PHASE,
  REORDER_TASKS
} from '../graphql/queries';
import {
  ArrowLeft,
  Edit2,
  Clock,
  Calendar,
  X,
  Check,
  Search,
  User,
  Lock,
  AlertTriangle,
  CheckCircle,
  Plus,
  Trash2,
  Paperclip,
  FileText,
  Image as ImageIcon,
  FileSpreadsheet,
  FileArchive,
  Edit,
  Download,
  MessageSquare,
  Eye,
  Layers,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Project, Client, Resource, Catalog, Label } from '../types';
import { useAuth } from '../context/AuthContext';

const getProjectWeight = (size?: string, level?: string): number => {
  if (!size) return 0;
  let normalizedLevel = 'Senior';
  if (level) {
    if (level === 'Junior' || level === 'Junior 1' || level === 'Junior 2' || level === 'Junior 3' || level.startsWith('Junior ')) {
      normalizedLevel = 'Junior';
    } else if (level === 'Mid' || level === 'Mid 1' || level === 'Mid 2' || level.startsWith('Mid ')) {
      normalizedLevel = 'Mid';
    }
  }
  
  const weights: Record<string, Record<string, number>> = {
    'XS': { 'Junior': 20, 'Mid': 15, 'Senior': 10 },
    'S':  { 'Junior': 25, 'Mid': 20, 'Senior': 15 },
    'M':  { 'Junior': 30, 'Mid': 25, 'Senior': 20 },
    'L':  { 'Junior': 50, 'Mid': 40, 'Senior': 30 },
    'XL': { 'Junior': 100, 'Mid': 75, 'Senior': 50 }
  };
  
  return weights[size]?.[normalizedLevel] || 0;
};

interface ProjectDeveloperInput {
  resourceId: string;
  role: string;
  name?: string;
  overloadReason?: string;
}

interface ProjectFormData {
  name?: string;
  description?: string;
  followUp?: boolean;
  clientId?: string;
  type?: string;
  status?: string;
  priority?: string;
  size?: string;
  estimate?: string;
  startDate?: string;
  dueDate?: string;
  region?: string;
  groupName?: string;
  inScope?: string;
  outOfScope?: string;
  expectedOutcomes?: string;
  keyFeaturesP0?: string;
  keyFeaturesP1?: string;
  keyFeaturesP2?: string;
}

function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userRole = user?.role ? user.role.toLowerCase() : '';
  const canManage = userRole === 'admin' || userRole === 'project_manager';
  const canWriteCommentsTasks = userRole === 'admin' || userRole === 'project_manager' || userRole === 'developer';
  
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [formData, setFormData] = useState<ProjectFormData>({});
  const [selectedDevelopers, setSelectedDevelopers] = useState<ProjectDeveloperInput[]>([]);
  const [modalTab, setModalTab] = useState<'general' | 'scope' | 'team'>('general');
  const [teamSearch, setTeamSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'assigned' | 'available' | 'overloaded'>('all');
  const [levelFilter, setLevelFilter] = useState<'all' | 'Junior' | 'Mid' | 'Senior'>('all');

  const { data, loading, refetch } = useQuery<{ project: Project }>(GET_PROJECT, { variables: { id } });
  const { data: clientsData } = useQuery<{ clients: { clients: Client[] } }>(GET_CLIENTS, { variables: { limit: 100 } });
  const { data: resourcesData } = useQuery<{ resources: { resources: Resource[] } }>(GET_RESOURCES, { variables: { limit: 100 } });
  const { data: typesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'projectType' } });
  const { data: statusesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'projectStatus' } });
  const { data: prioritiesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'priority' } });
  const { data: sizesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'size' } });

  const [updateProject] = useMutation(UPDATE_PROJECT, {
    onCompleted: () => {
      setShowEditModal(false);
      refetch();
    }
  });

  const [updateProjectDevelopers] = useMutation(UPDATE_PROJECT_DEVELOPERS);

  // Sub-modules mutations
  const [addTask] = useMutation(ADD_TASK, { onCompleted: () => refetch() });
  const [updateTask] = useMutation(UPDATE_TASK, { onCompleted: () => refetch() });
  const [reorderTasks] = useMutation(REORDER_TASKS, { onCompleted: () => refetch() });
  const [deleteTask] = useMutation(DELETE_TASK, { onCompleted: () => refetch() });
  const [addTaskComment] = useMutation(ADD_TASK_COMMENT, { onCompleted: () => refetch() });
  const [deleteTaskComment] = useMutation(DELETE_TASK_COMMENT, { onCompleted: () => refetch() });

  const [addNote] = useMutation(ADD_NOTE, { onCompleted: () => refetch() });
  const [updateNote] = useMutation(UPDATE_NOTE, { onCompleted: () => refetch() });
  const [deleteNote] = useMutation(DELETE_NOTE, { onCompleted: () => refetch() });

  const [addAsset] = useMutation(ADD_ASSET, { onCompleted: () => refetch() });
  const [deleteAsset] = useMutation(DELETE_ASSET, { onCompleted: () => refetch() });

  const [addPhase] = useMutation(ADD_PHASE, { onCompleted: () => refetch() });
  const [updatePhase] = useMutation(UPDATE_PHASE, { onCompleted: () => refetch() });
  const [deletePhase] = useMutation(DELETE_PHASE, { onCompleted: () => refetch() });

  // Sub-modules state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [taskForm, setTaskForm] = useState({
    name: '',
    description: '',
    assignedToId: '',
    status: 'Todo',
    dueDate: '',
    phaseId: '',
    taskType: 'Tarea',
    externalManager: ''
  });
  const [workstreamSubTab, setWorkstreamSubTab] = useState<'list' | 'roadmap'>('list');
  const [collapsedPhaseIds, setCollapsedPhaseIds] = useState<string[]>([]);
  const [draggedOverCol, setDraggedOverCol] = useState<string | null>(null);
  const [draggedOverPhase, setDraggedOverPhase] = useState<string | null>(null);
  const [draggedOverTaskId, setDraggedOverTaskId] = useState<string | null>(null);
  const [showRoadmapPreview, setShowRoadmapPreview] = useState(false);
  const [showListPreview, setShowListPreview] = useState(false);
  const [leftColWidth, setLeftColWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const container = document.getElementById('roadmap-container');
      if (container) {
        const rect = container.getBoundingClientRect();
        const newWidth = Math.max(150, Math.min(600, e.clientX - rect.left));
        setLeftColWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const formatCalendarDate = (dateVal: any, showYear: boolean = false, locale: string = 'es') => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    
    // To avoid timezone shifts, we use the UTC getters.
    const day = d.getUTCDate();
    const monthIndex = d.getUTCMonth();
    const year = d.getUTCFullYear();
    
    const monthsEs = [
      'ene', 'feb', 'mar', 'abr', 'may', 'jun',
      'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
    ];
    const monthsEn = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    const months = (locale === 'es' || locale.startsWith('es')) ? monthsEs : monthsEn;
    const monthName = months[monthIndex] || '';
    
    if (showYear) {
      if (locale === 'es' || locale.startsWith('es')) {
        return `${day} de ${monthName} ${year}`;
      }
      return `${monthName} ${day}, ${year}`;
    }
    
    if (locale === 'es' || locale.startsWith('es')) {
      return `${day} de ${monthName}`;
    }
    return `${monthName} ${day}`;
  };

  const getTaskStatusColor = (status: string) => {
    switch (status) {
      case 'Todo': return '#64748b';
      case 'In Progress': return '#f59e0b';
      case 'Review': return '#8b5cf6';
      case 'Done': return '#10b981';
      default: return '#64748b';
    }
  };

  const getTaskAssigneeInfo = (task: any) => ({
    type: task.taskType || 'Tarea',
    name: task.taskType === 'Insumo'
      ? (task.externalManager || 'N/A')
      : (task.assignedTo?.name || 'Sin Asignar'),
    isInput: task.taskType === 'Insumo'
  });

  const escapeHtml = (value: any) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const getPillbarHtml = (task: any) => {
    const info = getTaskAssigneeInfo(task);
    const typeStyle = info.isInput
      ? 'background: #fef3c7; color: #92400e; border: 1px solid #fde68a;'
      : 'background: #e0e7ff; color: #3730a3; border: 1px solid #c7d2fe;';
    return `
      <span style="display: inline-flex; align-items: center; gap: 4px; max-width: 100%;">
        <span style="${typeStyle} border-radius: 999px; padding: 2px 7px; font-size: 10px; font-weight: 700; line-height: 1.2; white-space: nowrap;">${escapeHtml(info.type)}</span>
        <span style="background: #f8fafc; color: #334155; border: 1px solid #cbd5e1; border-radius: 999px; padding: 2px 8px; font-size: 10px; font-weight: 600; line-height: 1.2; white-space: nowrap;">${escapeHtml(info.name)}</span>
      </span>
    `;
  };

  const drawPillbar = (
    ctx: CanvasRenderingContext2D,
    task: any,
    x: number,
    y: number,
    maxWidth: number = 240
  ) => {
    const info = getTaskAssigneeInfo(task);
    const font = 'bold 9px system-ui, -apple-system, sans-serif';
    const gap = 5;
    const pillHeight = 18;
    const radius = 9;
    const drawPill = (text: string, px: number, fill: string, stroke: string, color: string, availableWidth: number) => {
      ctx.font = font;
      let label = text;
      const paddingX = 8;
      let pillWidth = Math.ceil(ctx.measureText(label).width) + paddingX * 2;
      while (pillWidth > availableWidth && label.length > 4) {
        label = `${label.slice(0, -4)}...`;
        pillWidth = Math.ceil(ctx.measureText(label).width) + paddingX * 2;
      }
      pillWidth = Math.min(pillWidth, availableWidth);
      ctx.fillStyle = fill;
      ctx.strokeStyle = stroke;
      ctx.lineWidth = 1;
      ctx.beginPath();
      if ((ctx as any).roundRect) {
        (ctx as any).roundRect(px, y, pillWidth, pillHeight, radius);
      } else {
        ctx.rect(px, y, pillWidth, pillHeight);
      }
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.fillText(label, px + paddingX, y + 12);
      return pillWidth;
    };

    const typeWidth = drawPill(
      info.type,
      x,
      info.isInput ? '#fef3c7' : '#e0e7ff',
      info.isInput ? '#fde68a' : '#c7d2fe',
      info.isInput ? '#92400e' : '#3730a3',
      Math.min(72, maxWidth)
    );
    drawPill(info.name, x + typeWidth + gap, '#f8fafc', '#cbd5e1', '#334155', Math.max(60, maxWidth - typeWidth - gap));
  };

  const [newCommentText, setNewCommentText] = useState('');
  const [previewAsset, setPreviewAsset] = useState<any>(null);

  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [editingPhase, setEditingPhase] = useState<any>(null);
  const [phaseForm, setPhaseForm] = useState({ name: '', startDate: '', dueDate: '' });

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState<any>(null);
  const [noteForm, setNoteForm] = useState({ title: '', content: '' });

  const project = data?.project;

  const openEditModal = () => {
    if (project) {
      setModalTab('general');
      setTeamSearch('');
      setActiveFilter('all');
      setLevelFilter('all');
      setFormData({
        name: project.name || '',
        description: project.description || '',
        followUp: project.followUp || false,
        clientId: project.client?.id || '',
        type: project.type || '',
        status: project.status || 'Backlog',
        priority: project.priority || 'Medium',
        size: project.size || '',
        estimate: project.estimate || '',
        startDate: project.startDate ? project.startDate.split('T')[0] : '',
        dueDate: project.dueDate ? project.dueDate.split('T')[0] : '',
        region: project.region || '',
        groupName: project.groupName || '',
        inScope: project.inScope || '',
        outOfScope: project.outOfScope || '',
        expectedOutcomes: project.expectedOutcomes || '',
        keyFeaturesP0: project.keyFeaturesP0 || '',
        keyFeaturesP1: project.keyFeaturesP1 || '',
        keyFeaturesP2: project.keyFeaturesP2 || ''
      });
      setSelectedDevelopers(
        project.developers?.map(d => ({
          resourceId: d.resource?.id || '',
          role: d.role,
          name: d.resource?.name,
          overloadReason: d.overloadReason || ''
        })) || []
      );
      setShowEditModal(true);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    await updateProject({
      variables: {
        id,
        input: {
          ...formData,
          clientId: formData.clientId || null,
          startDate: formData.startDate ? new Date(formData.startDate).toISOString() : null,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
        }
      }
    });

    await updateProjectDevelopers({
      variables: {
        projectId: id,
        developers: selectedDevelopers.map(d => ({
          resourceId: d.resourceId,
          role: d.role || 'support',
          overloadReason: d.overloadReason || ''
        }))
      }
    });

    refetch();
  };

  // Sub-modules Handlers
  const handleTaskSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskForm.name.trim()) return;

    const input = {
      name: taskForm.name,
      description: taskForm.description,
      assignedToId: taskForm.taskType === 'Tarea' && taskForm.assignedToId ? taskForm.assignedToId : null,
      status: taskForm.status,
      dueDate: taskForm.dueDate ? new Date(taskForm.dueDate).toISOString() : null,
      phaseId: taskForm.phaseId || null,
      taskType: taskForm.taskType,
      externalManager: taskForm.taskType === 'Insumo' ? taskForm.externalManager : ''
    };

    if (editingTask) {
      await updateTask({
        variables: {
          projectId: id,
          taskId: editingTask.id,
          input
        }
      });
    } else {
      await addTask({
        variables: {
          projectId: id,
          input
        }
      });
    }

    setShowTaskModal(false);
    setEditingTask(null);
    setTaskForm({
      name: '',
      description: '',
      assignedToId: '',
      status: 'Todo',
      dueDate: '',
      phaseId: '',
      taskType: 'Tarea',
      externalManager: ''
    });
  };

  const handleEditTask = (task: any) => {
    setEditingTask(task);
    setTaskForm({
      name: task.name,
      description: task.description || '',
      assignedToId: task.assignedTo?.id || '',
      status: task.status,
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
      phaseId: task.phaseId || '',
      taskType: task.taskType || 'Tarea',
      externalManager: task.externalManager || ''
    });
    setShowTaskModal(true);
  };

  const handlePhaseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phaseForm.name.trim()) return;

    if (phaseForm.startDate && phaseForm.dueDate && new Date(phaseForm.startDate) > new Date(phaseForm.dueDate)) {
      alert('La fecha de inicio no puede ser posterior a la fecha de vencimiento');
      return;
    }

    const input = {
      name: phaseForm.name,
      startDate: phaseForm.startDate ? new Date(phaseForm.startDate).toISOString() : null,
      dueDate: phaseForm.dueDate ? new Date(phaseForm.dueDate).toISOString() : null
    };

    if (editingPhase) {
      await updatePhase({
        variables: {
          projectId: id,
          phaseId: editingPhase.id,
          input
        }
      });
    } else {
      await addPhase({
        variables: {
          projectId: id,
          input
        }
      });
    }

    setShowPhaseModal(false);
    setEditingPhase(null);
    setPhaseForm({ name: '', startDate: '', dueDate: '' });
  };

  const handleEditPhase = (phase: any) => {
    setEditingPhase(phase);
    setPhaseForm({
      name: phase.name,
      startDate: phase.startDate ? phase.startDate.split('T')[0] : '',
      dueDate: phase.dueDate ? phase.dueDate.split('T')[0] : ''
    });
    setShowPhaseModal(true);
  };

  const handleDeletePhase = async (phaseId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta fase? Las tareas contenidas en ella quedarán sin fase asignada.')) {
      await deletePhase({
        variables: {
          projectId: id,
          phaseId
        }
      });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta tarea?')) {
      await deleteTask({
        variables: {
          projectId: id,
          taskId
        }
      });
    }
  };

  const handleQuickMoveTask = async (task: any, newStatus: string) => {
    await updateTask({
      variables: {
        projectId: id,
        taskId: task.id,
        input: {
          name: task.name,
          assignedToId: task.assignedTo?.id || null,
          status: newStatus,
          dueDate: task.dueDate
        }
      }
    });
  };

  const handleExportRoadmapImage = () => {
    if (!project || !project.phases || project.phases.length === 0) return;

    const sortedPhases = [...(project.phases || [])].sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

    let timelineStart = project.startDate ? new Date(project.startDate) : new Date();
    let timelineEnd = project.dueDate ? new Date(project.dueDate) : new Date();

    sortedPhases.forEach(p => {
      if (p.startDate) {
        const d = new Date(p.startDate);
        if (d < timelineStart) timelineStart = d;
      }
      if (p.dueDate) {
        const d = new Date(p.dueDate);
        if (d > timelineEnd) timelineEnd = d;
      }
    });

    timelineStart = new Date(timelineStart.getTime() - 7 * 24 * 60 * 60 * 1000);
    timelineEnd = new Date(timelineEnd.getTime() + 7 * 24 * 60 * 60 * 1000);

    const totalDays = Math.ceil((timelineEnd.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));

    const getMonthMarkers = () => {
      const markers = [];
      const current = new Date(timelineStart);
      while (current <= timelineEnd) {
        markers.push(new Date(current));
        current.setMonth(current.getMonth() + 1);
      }
      return markers;
    };
    const monthMarkers = getMonthMarkers();

    const width = 1200;
    const headerHeight = 220;
    const phaseRowHeight = 55;
    const taskRowHeight = 40;
    const footerHeight = 40;

    let height = headerHeight + footerHeight;
    sortedPhases.forEach(phase => {
      height += phaseRowHeight;
      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      height += phaseTasks.length * taskRowHeight;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background - Light Mode theme
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Project Info Block
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(30, 20, width - 60, 90);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.strokeRect(30, 20, width - 60, 90);

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
    ctx.fillText('Información del Proyecto:', 45, 42);

    ctx.fillStyle = '#334155';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillText(`• Proyecto: ${project.name || 'N/A'}`, 45, 62);
    ctx.fillText(`• Cliente: ${project.client?.company || 'N/A'}`, 45, 80);
    ctx.fillText(`• Contacto: ${project.client?.name || 'N/A'}`, 45, 98);

    // Title
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
    ctx.fillText(`Roadmap: ${project.name || 'Proyecto'}`, 30, 145);

    // Subtitle
    ctx.fillStyle = '#475569';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillText('Cronograma General de Fases y Tareas', 30, 165);

    const tableLeftWidth = 420;
    const timelineWidth = width - tableLeftWidth - 60;
    const timelineX = tableLeftWidth + 30;

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, 195);
    ctx.lineTo(width - 30, 195);
    ctx.stroke();

    ctx.fillStyle = '#475569';
    ctx.font = 'bold 11px system-ui, -apple-system, sans-serif';
    ctx.fillText('FASE / TAREA', 30, 186);

    monthMarkers.forEach((markerDate) => {
      const timeDiff = markerDate.getTime() - timelineStart.getTime();
      const leftRatio = timeDiff / (1000 * 60 * 60 * 24) / totalDays;
      const x = timelineX + (leftRatio * timelineWidth);

      if (x >= timelineX && x <= timelineX + timelineWidth) {
        const monthLabel = markerDate.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }).toUpperCase();
        ctx.fillStyle = '#64748b';
        ctx.font = 'bold 9px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(monthLabel, x, 186);
        ctx.textAlign = 'left';

        ctx.strokeStyle = 'rgba(203, 213, 225, 0.4)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, 195);
        ctx.lineTo(x, height - footerHeight);
        ctx.stroke();
      }
    });

    let currentY = headerHeight;

    sortedPhases.forEach((phase, index) => {
      // Phase Background Row - Solid Non-Transparent
      ctx.fillStyle = '#f1f5f9';
      ctx.fillRect(30, currentY, width - 60, phaseRowHeight);

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
      let phaseName = phase.name || '';
      ctx.fillText(phaseName, 30, currentY + 22);

      ctx.fillStyle = '#64748b';
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      const dateText = `${formatCalendarDate(phase.startDate, false, 'es')} - ${formatCalendarDate(phase.dueDate, false, 'es')}`;
      ctx.fillText(dateText, 30, currentY + 38);

      if (phase.startDate && phase.dueDate) {
        const pStart = new Date(phase.startDate);
        const pEnd = new Date(phase.dueDate);
        const offset = Math.max(0, Math.ceil((pStart.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
        const duration = Math.max(1, Math.ceil((pEnd.getTime() - pStart.getTime()) / (1000 * 60 * 60 * 24)));

        const barLeftRatio = offset / totalDays;
        const barWidthRatio = duration / totalDays;

        const barX = timelineX + (barLeftRatio * timelineWidth);
        const barW = Math.max(12, barWidthRatio * timelineWidth);
        const barY = currentY + 16;
        const barH = 22;
        const r = 6;

        ctx.fillStyle = '#e2e8f0';
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1;

        ctx.beginPath();
        if ((ctx as any).roundRect) {
          (ctx as any).roundRect(barX, barY, barW, barH, r);
        } else {
          ctx.rect(barX, barY, barW, barH);
        }
        ctx.fill();
        ctx.stroke();

        const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
        const total = phaseTasks.length;
        const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

        if (pct > 0) {
          const fillW = (pct / 100) * barW;
          ctx.fillStyle = '#4f46e5';
          
          ctx.save();
          ctx.beginPath();
          if ((ctx as any).roundRect) {
            (ctx as any).roundRect(barX, barY, barW, barH, r);
          } else {
            ctx.rect(barX, barY, barW, barH);
          }
          ctx.clip();
          
          ctx.beginPath();
          if ((ctx as any).roundRect) {
            (ctx as any).roundRect(barX, barY, fillW, barH, [r, 0, 0, r]);
          } else {
            ctx.rect(barX, barY, fillW, barH);
          }
          ctx.fill();
          ctx.restore();
        }

        ctx.fillStyle = pct > 45 ? '#ffffff' : '#0f172a';
        ctx.font = 'bold 10px system-ui, -apple-system, sans-serif';
        ctx.fillText(`${pct}%`, barX + 8, barY + 14);
      }

      currentY += phaseRowHeight;

      // Draw task rows
      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      phaseTasks.forEach((task: any) => {
        // Task Name
        ctx.fillStyle = '#1e293b';
        ctx.font = '500 12px system-ui, -apple-system, sans-serif';
        let taskName = `↳ ${task.name || ''}`;
        ctx.fillText(taskName, 42, currentY + 18);

        drawPillbar(ctx, task, 42, currentY + 22, 300);

        // Draw thin task timeline bar
        if (task.dueDate) {
          const tStart = phase.startDate ? new Date(phase.startDate) : timelineStart;
          const tEnd = new Date(task.dueDate);
          const startVal = tStart < timelineStart ? timelineStart : tStart;
          const endVal = tEnd > timelineEnd ? timelineEnd : (tEnd < startVal ? startVal : tEnd);

          const barLeftRatio = Math.max(0, Math.ceil((startVal.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24))) / totalDays;
          const barWidthRatio = Math.max(1, Math.ceil((endVal.getTime() - startVal.getTime()) / (1000 * 60 * 60 * 24))) / totalDays;

          const barX = timelineX + (barLeftRatio * timelineWidth);
          const barW = Math.max(8, barWidthRatio * timelineWidth);
          const barY = currentY + 14;
          const barH = 10;
          const r = 3;

          ctx.fillStyle = getTaskStatusColor(task.status);
          ctx.beginPath();
          if ((ctx as any).roundRect) {
            (ctx as any).roundRect(barX, barY, barW, barH, r);
          } else {
            ctx.rect(barX, barY, barW, barH);
          }
          ctx.fill();
        }

        currentY += taskRowHeight;
      });
    });

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, height - footerHeight);
    ctx.lineTo(width - 30, height - footerHeight);
    ctx.stroke();

    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `roadmap-${project.name?.toLowerCase().replace(/\s+/g, '-') || 'proyecto'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleExportRoadmapPDF = () => {
    if (!project || !project.phases || project.phases.length === 0) return;

    const sortedPhases = [...(project.phases || [])].sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

    let timelineStart = project.startDate ? new Date(project.startDate) : new Date();
    let timelineEnd = project.dueDate ? new Date(project.dueDate) : new Date();

    sortedPhases.forEach(p => {
      if (p.startDate) {
        const d = new Date(p.startDate);
        if (d < timelineStart) timelineStart = d;
      }
      if (p.dueDate) {
        const d = new Date(p.dueDate);
        if (d > timelineEnd) timelineEnd = d;
      }
    });

    timelineStart = new Date(timelineStart.getTime() - 7 * 24 * 60 * 60 * 1000);
    timelineEnd = new Date(timelineEnd.getTime() + 7 * 24 * 60 * 60 * 1000);

    const totalDays = Math.ceil((timelineEnd.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));

    const getMonthMarkers = () => {
      const markers = [];
      const current = new Date(timelineStart);
      while (current <= timelineEnd) {
        markers.push(new Date(current));
        current.setMonth(current.getMonth() + 1);
      }
      return markers;
    };
    const monthMarkers = getMonthMarkers();
    const width = 1200;
    const headerHeight = 220;
    const phaseRowHeight = 55;
    const taskRowHeight = 40;
    const footerHeight = 40;

    let height = headerHeight + footerHeight;
    sortedPhases.forEach(phase => {
      height += phaseRowHeight;
      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      height += phaseTasks.length * taskRowHeight;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Light background for PDF/print
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Project Info Block
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(30, 20, width - 60, 90);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.strokeRect(30, 20, width - 60, 90);

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
    ctx.fillText('Información del Proyecto:', 45, 42);

    ctx.fillStyle = '#334155';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillText(`• Proyecto: ${project.name || 'N/A'}`, 45, 62);
    ctx.fillText(`• Cliente: ${project.client?.company || 'N/A'}`, 45, 80);
    ctx.fillText(`• Contacto: ${project.client?.name || 'N/A'}`, 45, 98);

    // Title
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
    ctx.fillText(`Roadmap: ${project.name || 'Proyecto'}`, 30, 145);

    ctx.fillStyle = '#475569';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillText('Cronograma General de Fases y Tareas', 30, 165);

    const tableLeftWidth = 420;
    const timelineWidth = width - tableLeftWidth - 60;
    const timelineX = tableLeftWidth + 30;

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, 195);
    ctx.lineTo(width - 30, 195);
    ctx.stroke();

    ctx.fillStyle = '#475569';
    ctx.font = 'bold 11px system-ui, -apple-system, sans-serif';
    ctx.fillText('FASE / TAREA', 30, 186);

    monthMarkers.forEach((markerDate) => {
      const timeDiff = markerDate.getTime() - timelineStart.getTime();
      const leftRatio = timeDiff / (1000 * 60 * 60 * 24) / totalDays;
      const x = timelineX + (leftRatio * timelineWidth);

      if (x >= timelineX && x <= timelineX + timelineWidth) {
        const monthLabel = markerDate.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }).toUpperCase();
        ctx.fillStyle = '#64748b';
        ctx.font = 'bold 9px system-ui, -apple-system, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(monthLabel, x, 186);
        ctx.textAlign = 'left';

        ctx.strokeStyle = 'rgba(203, 213, 225, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, 195);
        ctx.lineTo(x, height - footerHeight);
        ctx.stroke();
      }
    });

    let currentY = headerHeight;

    sortedPhases.forEach((phase, index) => {
      // Phase Background Row
      ctx.fillStyle = 'rgba(241, 245, 249, 0.6)';
      ctx.fillRect(30, currentY, width - 60, phaseRowHeight);

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
      let phaseName = phase.name || '';
      ctx.fillText(phaseName, 30, currentY + 22);

      ctx.fillStyle = '#64748b';
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      const dateText = `${formatCalendarDate(phase.startDate, false, 'es')} - ${formatCalendarDate(phase.dueDate, false, 'es')}`;
      ctx.fillText(dateText, 30, currentY + 38);

      if (phase.startDate && phase.dueDate) {
        const pStart = new Date(phase.startDate);
        const pEnd = new Date(phase.dueDate);
        const offset = Math.max(0, Math.ceil((pStart.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
        const duration = Math.max(1, Math.ceil((pEnd.getTime() - pStart.getTime()) / (1000 * 60 * 60 * 24)));

        const barLeftRatio = offset / totalDays;
        const barWidthRatio = duration / totalDays;

        const barX = timelineX + (barLeftRatio * timelineWidth);
        const barW = Math.max(12, barWidthRatio * timelineWidth);
        const barY = currentY + 16;
        const barH = 22;
        const r = 6;

        ctx.fillStyle = '#f1f5f9';
        ctx.strokeStyle = '#cbd5e1';
        ctx.lineWidth = 1;

        ctx.beginPath();
        if ((ctx as any).roundRect) {
          (ctx as any).roundRect(barX, barY, barW, barH, r);
        } else {
          ctx.rect(barX, barY, barW, barH);
        }
        ctx.fill();
        ctx.stroke();

        const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
        const total = phaseTasks.length;
        const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

        if (pct > 0) {
          const fillW = (pct / 100) * barW;
          ctx.fillStyle = '#4f46e5';
          
          ctx.save();
          ctx.beginPath();
          if ((ctx as any).roundRect) {
            (ctx as any).roundRect(barX, barY, barW, barH, r);
          } else {
            ctx.rect(barX, barY, barW, barH);
          }
          ctx.clip();
          
          ctx.beginPath();
          if ((ctx as any).roundRect) {
            (ctx as any).roundRect(barX, barY, fillW, barH, [r, 0, 0, r]);
          } else {
            ctx.rect(barX, barY, fillW, barH);
          }
          ctx.fill();
          ctx.restore();
        }

        ctx.fillStyle = pct > 45 ? '#ffffff' : '#0f172a';
        ctx.font = 'bold 10px system-ui, -apple-system, sans-serif';
        ctx.fillText(`${pct}%`, barX + 8, barY + 14);
      }

      currentY += phaseRowHeight;

      // Draw task rows
      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      phaseTasks.forEach((task: any) => {
        // Task Name
        ctx.fillStyle = '#1e293b';
        ctx.font = '500 12px system-ui, -apple-system, sans-serif';
        let taskName = `↳ ${task.name || ''}`;
        ctx.fillText(taskName, 42, currentY + 18);

        drawPillbar(ctx, task, 42, currentY + 22, 300);

        // Task Gantt bar
        if (task.dueDate) {
          const tStart = phase.startDate ? new Date(phase.startDate) : timelineStart;
          const tEnd = new Date(task.dueDate);
          const startVal = tStart < timelineStart ? timelineStart : tStart;
          const endVal = tEnd > timelineEnd ? timelineEnd : (tEnd < startVal ? startVal : tEnd);

          const barLeftRatio = Math.max(0, Math.ceil((startVal.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24))) / totalDays;
          const barWidthRatio = Math.max(1, Math.ceil((endVal.getTime() - startVal.getTime()) / (1000 * 60 * 60 * 24))) / totalDays;

          const barX = timelineX + (barLeftRatio * timelineWidth);
          const barW = Math.max(8, barWidthRatio * timelineWidth);
          const barY = currentY + 14;
          const barH = 10;
          const r = 3;

          ctx.fillStyle = getTaskStatusColor(task.status);
          ctx.beginPath();
          if ((ctx as any).roundRect) {
            (ctx as any).roundRect(barX, barY, barW, barH, r);
          } else {
            ctx.rect(barX, barY, barW, barH);
          }
          ctx.fill();
        }

        currentY += taskRowHeight;
      });
    });

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, height - footerHeight);
    ctx.lineTo(width - 30, height - footerHeight);
    ctx.stroke();



    const imageUrl = canvas.toDataURL('image/png');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const phasesHtml = sortedPhases.map(phase => {
      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      const total = phaseTasks.length;
      const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
      const tasksList = phaseTasks.map((t: any) => `
        <li style="margin-bottom: 6px; font-size: 13px;">
          <strong>[${t.status}]</strong> ${t.name}
          ${t.dueDate ? `<span style="color: #64748b; font-size: 11px;"> - Vence: ${formatCalendarDate(t.dueDate, false, 'es')}</span>` : ''}
          <span style="display: inline-block; margin-left: 6px;">${getPillbarHtml(t)}</span>
        </li>
      `).join('');

      return `
        <div style="margin-bottom: 24px; page-break-inside: avoid;">
          <h3 style="margin-bottom: 6px; color: #0f172a; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px;">
            ${phase.name}
            <span style="font-size: 13px; font-weight: normal; color: #64748b;">
              (${formatCalendarDate(phase.startDate, true, 'es')} - ${formatCalendarDate(phase.dueDate, true, 'es')})
            </span>
          </h3>
          <p style="margin: 4px 0 10px 0; font-size: 13px; color: #475569;">
            ${completed} de ${total} tareas completadas (${total > 0 ? Math.round((completed / total) * 100) : 0}%)
          </p>
          <ul style="margin: 0; padding-left: 20px; color: #334155;">
            ${tasksList || '<li style="color: #94a3b8; font-style: italic; font-size: 12px;">Sin tareas asignadas</li>'}
          </ul>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Roadmap PDF - ${project.name || 'Proyecto'}</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              color: #1e293b;
              margin: 40px;
              line-height: 1.5;
            }
            @media print {
              body { margin: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <div>
              <h1 style="margin: 0; font-size: 24px; color: #0f172a;">${project.name || 'Proyecto'}</h1>
              <p style="margin: 4px 0 0 0; color: #475569; font-size: 14px;">Reporte y Cronograma de Trabajo</p>
            </div>
            <button class="no-print" onclick="window.print()" style="padding: 8px 16px; background: #4f46e5; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600;">
              Imprimir / Guardar PDF
            </button>
          </div>

          <div style="margin-bottom: 24px; border: 1px solid #cbd5e1; padding: 16px; border-radius: 8px; background: #f8fafc;">
            <h3 style="margin: 0 0 10px 0; font-size: 14px; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px;">Información del Proyecto:</h3>
            <div style="font-size: 12px; line-height: 1.6; color: #334155;">
              <div><strong>• Proyecto:</strong> ${project.name || 'N/A'}</div>
              <div><strong>• Cliente:</strong> ${project.client?.company || 'N/A'}</div>
              <div><strong>• Contacto:</strong> ${project.client?.name || 'N/A'}</div>
            </div>
          </div>

          <div style="margin-bottom: 30px; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
            <img src="${imageUrl}" style="width: 100%; display: block;" alt="Roadmap Timeline" />
          </div>

          <h2 style="margin-top: 40px; margin-bottom: 20px; color: #0f172a; border-bottom: 2px solid #0f172a; padding-bottom: 6px; font-size: 18px;">
            Detalle de Fases y Tareas
          </h2>
          <div>
            ${phasesHtml}
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleExportListImage = () => {
    if (!project || !project.phases || project.phases.length === 0) return;

    const sortedPhases = [...(project.phases || [])].sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

    const width = 1200;
    const headerHeight = 220;
    const phaseRowHeight = 55;
    const taskRowHeight = 40;
    const footerHeight = 40;

    let height = headerHeight + footerHeight;
    sortedPhases.forEach(phase => {
      height += phaseRowHeight;
      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      height += phaseTasks.length * taskRowHeight;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background - Light Mode theme
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // Project Info Block
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(30, 20, width - 60, 90);
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.strokeRect(30, 20, width - 60, 90);

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
    ctx.fillText('Información del Proyecto:', 45, 42);

    ctx.fillStyle = '#334155';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillText(`• Proyecto: ${project.name || 'N/A'}`, 45, 62);
    ctx.fillText(`• Cliente: ${project.client?.company || 'N/A'}`, 45, 80);
    ctx.fillText(`• Contacto: ${project.client?.name || 'N/A'}`, 45, 98);

    // Title
    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 18px system-ui, -apple-system, sans-serif';
    ctx.fillText(`Lista de Fases: ${project.name || 'Proyecto'}`, 30, 145);

    // Subtitle
    ctx.fillStyle = '#475569';
    ctx.font = '12px system-ui, -apple-system, sans-serif';
    ctx.fillText('Reporte Detallado de Trabajo por Fases y Tareas', 30, 165);

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, 195);
    ctx.lineTo(width - 30, 195);
    ctx.stroke();

    ctx.fillStyle = '#475569';
    ctx.font = 'bold 11px system-ui, -apple-system, sans-serif';
    ctx.fillText('FASE / TAREA', 40, 186);
    ctx.fillText('FECHAS', 500, 186);
    ctx.fillText('ESTADO', 720, 186);
    ctx.fillText('RESPONSABLE', 920, 186);

    let currentY = headerHeight;

    sortedPhases.forEach((phase) => {
      ctx.fillStyle = '#f1f5f9';
      ctx.fillRect(30, currentY, width - 60, phaseRowHeight);

      // Border for phase card
      ctx.strokeStyle = '#cbd5e1';
      ctx.lineWidth = 1;
      ctx.strokeRect(30, currentY, width - 60, phaseRowHeight);

      ctx.fillStyle = '#0f172a';
      ctx.font = 'bold 13px system-ui, -apple-system, sans-serif';
      ctx.fillText(phase.name || '', 40, currentY + 22);

      ctx.fillStyle = '#334155';
      ctx.font = '11px system-ui, -apple-system, sans-serif';
      const phaseDates = `${formatCalendarDate(phase.startDate, true, 'es')} - ${formatCalendarDate(phase.dueDate, true, 'es')}`;
      ctx.fillText(phaseDates, 500, currentY + 22);

      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      const total = phaseTasks.length;
      const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
      const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

      ctx.fillStyle = '#334155';
      ctx.font = 'bold 11px system-ui, -apple-system, sans-serif';
      ctx.fillText(`${completed} de ${total} compl. (${pct}%)`, 720, currentY + 22);

      ctx.fillStyle = '#cbd5e1';
      ctx.fillRect(720, currentY + 30, 120, 6);
      if (pct > 0) {
        ctx.fillStyle = '#4f46e5';
        ctx.fillRect(720, currentY + 30, (pct / 100) * 120, 6);
      }

      currentY += phaseRowHeight;

      phaseTasks.forEach((task: any) => {
        // Thin bottom border for task rows
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(30, currentY + taskRowHeight);
        ctx.lineTo(width - 30, currentY + taskRowHeight);
        ctx.stroke();

        ctx.fillStyle = '#1e293b';
        ctx.font = '500 12px system-ui, -apple-system, sans-serif';
        ctx.fillText(`↳ ${task.name || ''}`, 52, currentY + 18);

        ctx.fillStyle = '#475569';
        ctx.font = '11px system-ui, -apple-system, sans-serif';
        const taskDueDate = task.dueDate ? formatCalendarDate(task.dueDate, false, 'es') : '-';
        ctx.fillText(taskDueDate, 500, currentY + 18);

        ctx.fillStyle = '#475569';
        ctx.font = '11px system-ui, -apple-system, sans-serif';
        ctx.fillText(task.status || '', 720, currentY + 18);

        ctx.fillStyle = getTaskStatusColor(task.status);
        ctx.beginPath();
        ctx.arc(710, currentY + 14, 4, 0, 2 * Math.PI);
        ctx.fill();

        drawPillbar(ctx, task, 920, currentY + 8, 230);

        currentY += taskRowHeight;
      });
    });

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, height - footerHeight);
    ctx.lineTo(width - 30, height - footerHeight);
    ctx.stroke();

    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `lista-fases-${project.name?.toLowerCase().replace(/\s+/g, '-') || 'proyecto'}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleExportListPDF = () => {
    if (!project || !project.phases || project.phases.length === 0) return;

    const sortedPhases = [...(project.phases || [])].sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const phasesHtml = sortedPhases.map(phase => {
      const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
      const total = phaseTasks.length;
      const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
      const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
      
      const tasksRows = phaseTasks.map((t: any) => `
        <tr style="border-bottom: 1px solid #e2e8f0; font-size: 13px;">
          <td style="padding: 10px; color: #1e293b;">↳ ${t.name}</td>
          <td style="padding: 10px; color: #475569;">${t.dueDate ? formatCalendarDate(t.dueDate, false, 'es') : '-'}</td>
          <td style="padding: 10px;">
            <span style="display: inline-flex; align-items: center; gap: 6px;">
              <span style="width: 8px; height: 8px; border-radius: 50%; background-color: ${getTaskStatusColor(t.status)};"></span>
              ${t.status}
            </span>
          </td>
          <td style="padding: 10px;">
            ${getPillbarHtml(t)}
          </td>
        </tr>
      `).join('');

      return `
        <div style="margin-bottom: 30px; page-break-inside: avoid;">
          <div style="background: #f1f5f9; padding: 12px 16px; border-radius: 6px; display: flex; justify-content: space-between; align-items: center; border: 1px solid #cbd5e1; margin-bottom: 12px;">
            <div>
              <h3 style="margin: 0; font-size: 16px; color: #0f172a;">${phase.name}</h3>
              <span style="font-size: 12px; color: #64748b;">
                ${formatCalendarDate(phase.startDate, true, 'es')} - ${formatCalendarDate(phase.dueDate, true, 'es')}
              </span>
            </div>
            <div style="text-align: right;">
              <span style="font-size: 14px; font-weight: bold; color: #4f46e5;">${pct}% Completado</span>
              <div style="font-size: 11px; color: #64748b; margin-top: 2px;">${completed} de ${total} tareas completadas</div>
            </div>
          </div>

          <table style="width: 100%; border-collapse: collapse; text-align: left;">
            <thead>
              <tr style="background: #f8fafc; border-bottom: 2px solid #cbd5e1; font-size: 12px; color: #475569; font-weight: bold;">
                <th style="padding: 8px 10px;">Tarea</th>
                <th style="padding: 8px 10px;">Fechas</th>
                <th style="padding: 8px 10px;">Estado</th>
                <th style="padding: 8px 10px;">Responsable</th>
              </tr>
            </thead>
            <tbody>
              ${tasksRows || `<tr><td colspan="4" style="padding: 12px; text-align: center; color: #94a3b8; font-style: italic; font-size: 12px;">Sin tareas asignadas</td></tr>`}
            </tbody>
          </table>
        </div>
      `;
    }).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>Reporte de Fases - ${project.name || 'Proyecto'}</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              color: #1e293b;
              margin: 40px;
              line-height: 1.5;
            }
            @media print {
              body { margin: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 2px solid #0f172a; padding-bottom: 12px;">
            <div>
              <h1 style="margin: 0; font-size: 24px; color: #0f172a;">${project.name || 'Proyecto'}</h1>
              <p style="margin: 4px 0 0 0; color: #475569; font-size: 14px;">Lista Detallada de Fases y Tareas</p>
            </div>
            <button class="no-print" onclick="window.print()" style="padding: 8px 16px; background: #4f46e5; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600;">
              Imprimir / Guardar PDF
            </button>
          </div>

          <div style="margin-bottom: 24px; border: 1px solid #cbd5e1; padding: 16px; border-radius: 8px; background: #f8fafc;">
            <h3 style="margin: 0 0 10px 0; font-size: 14px; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 6px;">Información del Proyecto:</h3>
            <div style="font-size: 12px; line-height: 1.6; color: #334155;">
              <div><strong>• Proyecto:</strong> ${project.name || 'N/A'}</div>
              <div><strong>• Cliente:</strong> ${project.client?.company || 'N/A'}</div>
              <div><strong>• Contacto:</strong> ${project.client?.name || 'N/A'}</div>
            </div>
          </div>

          <div>
            ${phasesHtml}
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 300);
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };



  const handleAddComment = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!newCommentText.trim() || !editingTask) return;

    await addTaskComment({
      variables: {
        projectId: id,
        taskId: editingTask.id,
        content: newCommentText.trim()
      }
    });

    setNewCommentText('');
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!editingTask) return;
    if (window.confirm('¿Estás seguro de que deseas eliminar este comentario?')) {
      await deleteTaskComment({
        variables: {
          projectId: id,
          taskId: editingTask.id,
          commentId
        }
      });
    }
  };

  const handleNoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noteForm.title.trim() || !noteForm.content.trim()) return;

    if (editingNote) {
      await updateNote({
        variables: {
          projectId: id,
          noteId: editingNote.id,
          input: {
            title: noteForm.title,
            content: noteForm.content
          }
        }
      });
    } else {
      await addNote({
        variables: {
          projectId: id,
          input: {
            title: noteForm.title,
            content: noteForm.content
          }
        }
      });
    }

    setShowNoteModal(false);
    setEditingNote(null);
    setNoteForm({ title: '', content: '' });
  };

  const handleEditNote = (note: any) => {
    setEditingNote(note);
    setNoteForm({
      title: note.title,
      content: note.content
    });
    setShowNoteModal(true);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta nota?')) {
      await deleteNote({
        variables: {
          projectId: id,
          noteId
        }
      });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canManage) return;
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size limit: 10MB max for base64 files
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('El archivo supera el límite de tamaño permitido (10 MB).');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64Data = reader.result as string;
      
      let fileSize = `${(file.size / 1024).toFixed(1)} KB`;
      if (file.size > 1024 * 1024) {
        fileSize = `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
      }

      let fileType = 'other';
      if (file.type.startsWith('image/')) fileType = 'image';
      else if (file.type === 'application/pdf') fileType = 'pdf';
      else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) fileType = 'excel';
      else if (file.name.endsWith('.doc') || file.name.endsWith('.docx')) fileType = 'doc';

      try {
        await addAsset({
          variables: {
            projectId: id,
            input: {
              name: file.name,
              url: base64Data,
              fileType,
              fileSize
            }
          }
        });
      } catch (err: any) {
        console.error('Error uploading file:', err);
        alert('Error al subir el archivo. Es posible que el archivo sea demasiado grande o que el servidor haya rechazado la solicitud.');
      }
    };

    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleDeleteAsset = async (assetId: string) => {
    if (!canManage) return;
    if (window.confirm('¿Estás seguro de que deseas eliminar este archivo?')) {
      await deleteAsset({
        variables: {
          projectId: id,
          assetId
        }
      });
    }
  };

  const getAssetIcon = (type?: string) => {
    switch (type) {
      case 'pdf':
        return <FileText size={20} />;
      case 'image':
        return <ImageIcon size={20} />;
      case 'excel':
        return <FileSpreadsheet size={20} />;
      case 'doc':
        return <FileText size={20} />;
      default:
        return <Paperclip size={20} />;
    }
  };

  const toggleDeveloper = (resource: Partial<Resource>) => {
    if (!resource.id) return;
    const exists = selectedDevelopers.find(d => d.resourceId === resource.id);
    if (exists) {
      setSelectedDevelopers(selectedDevelopers.filter(d => d.resourceId !== resource.id));
    } else {
      const currentSize = formData.size || project?.size || 'M';
      const projectWeight = getProjectWeight(currentSize, resource.level);
      const isCurrentlyAssigned = project?.developers?.some(d => d.resource?.id === resource.id) || false;
      const currentResourceLoad = resource.load !== undefined ? parseFloat(String(resource.load)) : 0;
      const baseLoad = isCurrentlyAssigned ? currentResourceLoad - projectWeight : currentResourceLoad;
      
      if (baseLoad > 100) {
        return; // Locked - already overloaded by other projects
      }
      
      setSelectedDevelopers([...selectedDevelopers, {
        resourceId: resource.id,
        role: 'support',
        name: resource.name,
        overloadReason: ''
      }]);
    }
  };

  const updateOverloadReason = (resourceId: string, reason: string) => {
    setSelectedDevelopers(selectedDevelopers.map(d =>
      d.resourceId === resourceId ? { ...d, overloadReason: reason } : d
    ));
  };

  const hasInvalidOverloads = () => {
    return selectedDevelopers.some(d => {
      const resource = resourcesData?.resources?.resources?.find(r => r.id === d.resourceId);
      if (!resource) return false;
      const currentSize = formData.size || project?.size || 'M';
      const projectWeight = getProjectWeight(currentSize, resource.level);
      const isCurrentlyAssigned = project?.developers?.some(dev => dev.resource?.id === resource.id) || false;
      const currentResourceLoad = resource.load !== undefined ? parseFloat(String(resource.load)) : 0;
      const baseLoad = isCurrentlyAssigned ? currentResourceLoad - projectWeight : currentResourceLoad;
      const newLoad = baseLoad + projectWeight;

      return newLoad > 100 && (!d.overloadReason || !d.overloadReason.trim());
    });
  };

  const getInitials = (name?: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getAvatarColor = (name?: string) => {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
    const index = name?.charCodeAt(0) ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysRemaining = (dueDate?: string) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusColor = (status?: string) => {
    if (!status) return '#64748B';
    const found = statusesData?.catalogs?.find(
      c => c.name.toLowerCase() === status.toLowerCase()
    );
    return found?.color || '#64748B';
  };

  const getTypeColor = (typeName: string) => {
    const found = typesData?.catalogs?.find(c => c.name === typeName);
    return found?.color || '#4f46e5';
  };

  const getSizeColor = (sizeName: string) => {
    const found = sizesData?.catalogs?.find(c => c.name === sizeName);
    return found?.color || '#4f46e5';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page-content">
        <div className="empty-state">
          <h3>Project not found</h3>
          <Link to="/projects" className="btn btn-primary" style={{ marginTop: '16px' }}>
            Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining(project.dueDate);
  const leadDev = project.developers?.find(d => d.role === 'lead');
  const supportDevs = project.developers?.filter(d => d.role === 'support') || [];

  const tabs = ['Overview', 'Workstream', 'Tasks', 'Notes', 'Assets & Files'];

  return (
    <>
      <div className="page-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
          <Link to="/projects" style={{ color: 'var(--text-muted)' }}>Projects</Link>
          <span style={{ color: 'var(--text-muted)' }}>&gt;</span>
          <span>{project.name}</span>
          <div style={{ flex: 1 }} />
          {canManage && (
            <button className="btn-icon" onClick={openEditModal}>
              <Edit2 size={18} />
            </button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h1 className="page-title" style={{ marginBottom: 0 }}>{project.name}</h1>
          <span 
            className="badge"
            style={{
              backgroundColor: `${getStatusColor(project.status)}15`,
              color: getStatusColor(project.status),
              border: `1px solid ${getStatusColor(project.status)}25`
            }}
          >
            {project.status}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px', color: 'var(--text-muted)' }}>
          <span style={{ marginRight: '8px' }}>ID: #{project.id?.slice(-4)}</span>
          {project.client?.company && (
            <span className="badge-pill">
              Cliente: {project.client.company}
            </span>
          )}
          {project.type && (
            <span className="badge-pill" style={{ 
              '--pill-bg': `${getTypeColor(project.type)}1a`, 
              '--pill-color': getTypeColor(project.type),
              '--pill-border': `${getTypeColor(project.type)}33`
            } as React.CSSProperties}>
              Tipo: {project.type}
            </span>
          )}
          {project.size && (
            <span className="badge-pill" style={{ 
              '--pill-bg': `${getSizeColor(project.size)}1a`, 
              '--pill-color': getSizeColor(project.size),
              '--pill-border': `${getSizeColor(project.size)}33`
            } as React.CSSProperties}>
              Talla: {project.size}
            </span>
          )}
          <span style={{ margin: '0 4px' }}>•</span>
          <span>Prioridad: {project.priority}</span>
          <span style={{ margin: '0 4px' }}>•</span>
          <span>Región: {project.region}</span>
          <span style={{ margin: '0 4px' }}>•</span>
          <span>Última Sinc: {formatDate(project.updatedAt)}</span>
        </div>
      </div>

      <div className="page-content">
        {project.followUp && (
          <div className="alert alert-info" style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Check size={16} /> <span>This is a Follow Up project</span>
          </div>
        )}
        <div className="tabs">
          {tabs.map(tab => (
            <button
              key={tab}
              className={`tab ${activeTab === tab.toLowerCase().replace(/ & /g, '-') ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.toLowerCase().replace(/ & /g, '-'))}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="project-detail">
            <div className="project-detail-main">
              <div className="detail-card">
                <h3>Description</h3>
                <p style={{ color: 'var(--text-secondary)' }}>{project.description || 'No description'}</p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                <div className="detail-card">
                  <h3>In scope</h3>
                  <ul style={{ paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                    {project.inScope?.split('\n').filter(s => s.trim()).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="detail-card">
                  <h3>Out of scope</h3>
                  <ul style={{ paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                    {project.outOfScope?.split('\n').filter(s => s.trim()).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="detail-card">
                <h3>Expected Outcomes</h3>
                <ul style={{ paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                  {project.expectedOutcomes?.split('\n').filter(s => s.trim()).map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="detail-card">
                <h3>Key features</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <div>
                    <strong>P0:</strong>
                    <ul style={{ paddingLeft: '20px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                      {project.keyFeaturesP0?.split('\n').filter(s => s.trim()).map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <strong>P1:</strong>
                    <ul style={{ paddingLeft: '20px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                      {project.keyFeaturesP1?.split('\n').filter(s => s.trim()).map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <strong>P2:</strong>
                    <ul style={{ paddingLeft: '20px', color: 'var(--text-secondary)', marginTop: '8px' }}>
                      {project.keyFeaturesP2?.split('\n').filter(s => s.trim()).map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="project-detail-sidebar">
              <div className="detail-card">
                <h3>Time</h3>
                <div className="detail-row">
                  <span className="detail-label"><Clock size={14} /> Estimate</span>
                  <span className="detail-value">{project.estimate || '-'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><Calendar size={14} /> Start Date</span>
                  <span className="detail-value">{formatDate(project.startDate)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"><Calendar size={14} /> Due Date</span>
                  <span className="detail-value">{formatDate(project.dueDate)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Days remaining</span>
                  <span className="detail-value" style={{ color: (daysRemaining !== null && daysRemaining < 0) ? 'var(--danger)' : 'var(--success)' }}>
                    {daysRemaining !== null ? `${daysRemaining} Days to go` : '-'}
                  </span>
                </div>
                {project.dueDate && daysRemaining !== null && (
                  <div className="progress-bar" style={{ marginTop: '12px' }}>
                    <div
                      className="progress-fill"
                      style={{
                        width: `${Math.min(100, Math.max(0, 100 - (daysRemaining / 30 * 100)))}%`,
                        background: daysRemaining < 0 ? 'var(--danger)' : 'var(--primary)'
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="detail-card">
                <h3>Backlog</h3>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span 
                    className="badge"
                    style={{
                      backgroundColor: `${getStatusColor(project.status)}15`,
                      color: getStatusColor(project.status),
                      border: `1px solid ${getStatusColor(project.status)}25`
                    }}
                  >
                    {project.status}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Group</span>
                  <span className="detail-value">{project.groupName || 'None'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Priority</span>
                  <span className="detail-value">{project.priority}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Label</span>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {project.labels?.map((label: Label) => (
                      <span
                        key={label.id}
                        className="badge"
                        style={{
                          backgroundColor: label.color || 'var(--primary-light)',
                          color: 'var(--text-dark)'
                        }}
                      >
                        {label.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="detail-card">
                <h3>Developers</h3>
                <div className="detail-row">
                  <span className="detail-label">Lead</span>
                  {leadDev ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        className="avatar avatar-sm"
                        style={{ 
                          background: getAvatarColor(leadDev.resource?.name),
                          border: (leadDev.resource?.load !== undefined && parseFloat(String(leadDev.resource.load)) > 100) ? '2px solid var(--danger)' : 'none'
                        }}
                        title={`${leadDev.resource?.name}${leadDev.resource?.load !== undefined && parseFloat(String(leadDev.resource.load)) > 100 ? ' (Sobrecargado)' : ''}`}
                      >
                        {getInitials(leadDev.resource?.name)}
                      </div>
                      <span>{leadDev.resource?.name}</span>
                      {leadDev.resource?.load !== undefined && parseFloat(String(leadDev.resource.load)) > 100 && (
                        <span className="badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-text)', fontSize: '10px', padding: '2px 8px', marginLeft: '6px' }} title={`Motivo: ${leadDev.overloadReason || 'Sin especificar'}`}>
                          Sobrecargado
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="detail-value">-</span>
                  )}
                </div>
                <div className="detail-row">
                  <span className="detail-label">Support</span>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {supportDevs.map((dev: any, idx: number) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px' }}>
                        <div
                          className="avatar avatar-sm"
                          style={{
                            background: getAvatarColor(dev.resource?.name),
                            border: (dev.resource?.load !== undefined && parseFloat(String(dev.resource.load)) > 100) ? '2px solid var(--danger)' : 'none'
                          }}
                          title={`${dev.resource?.name}${dev.resource?.load !== undefined && parseFloat(String(dev.resource.load)) > 100 ? ` (Sobrecargado: ${dev.overloadReason || 'Sin especificar'})` : ''}`}
                        >
                          {getInitials(dev.resource?.name)}
                        </div>
                        {dev.resource?.load !== undefined && parseFloat(String(dev.resource.load)) > 100 && (
                          <span className="badge" style={{ background: 'var(--danger-light)', color: 'var(--danger-text)', fontSize: '10px', padding: '2px 8px' }} title={`Motivo: ${dev.overloadReason || 'Sin especificar'}`}>
                            Sobrecargado
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="detail-card">
                <h3>Client</h3>
                {project.client ? (
                  <>
                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>{project.client.company}</div>
                    <div className="detail-row">
                      <span className="detail-label">Contact</span>
                      <span className="detail-value">{project.client.name}</span>
                    </div>
                    {project.client.email && (
                      <div className="detail-row">
                        <span className="detail-label">Email</span>
                        <span className="detail-value">{project.client.email}</span>
                      </div>
                    )}
                    {project.client.country && (
                      <div className="detail-row">
                        <span className="detail-label">Country</span>
                        <span className="detail-value">{project.client.country}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-muted">No client info</div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workstream' && (
          <div className="workstream-view">
            <div className="workstream-header">
              <div className="workstream-title-group">
                <div className="workstream-title-kicker">
                  <Layers size={14} />
                  Workstream
                </div>
                <h2>Fases y etapas del proyecto</h2>
                <p>Organiza fases, tareas e insumos en una secuencia clara para revisar avance, fechas y responsables.</p>
              </div>
              <div className="workstream-header-right">
                <div className="workstream-stats">
                  <div className="workstream-stat">
                    <span>{project.phases?.length || 0}</span>
                    <small>Fases</small>
                  </div>
                  <div className="workstream-stat">
                    <span>{project.tasks?.length || 0}</span>
                    <small>Tareas</small>
                  </div>
                  <div className="workstream-stat">
                    <span>{project.tasks?.length ? Math.round(((project.tasks?.filter((t: any) => t.status === 'Done').length || 0) / project.tasks.length) * 100) : 0}%</span>
                    <small>Avance</small>
                  </div>
                </div>

                <div className="workstream-actions">
                  <div className="workstream-segmented-control" role="tablist" aria-label="Vista de Workstream">
                    <button
                      type="button"
                      className={`workstream-view-toggle ${workstreamSubTab === 'list' ? 'active' : ''}`}
                      onClick={() => setWorkstreamSubTab('list')}
                      role="tab"
                      aria-selected={workstreamSubTab === 'list'}
                    >
                      Lista
                    </button>
                    <button
                      type="button"
                      className={`workstream-view-toggle ${workstreamSubTab === 'roadmap' ? 'active' : ''}`}
                      onClick={() => setWorkstreamSubTab('roadmap')}
                      role="tab"
                      aria-selected={workstreamSubTab === 'roadmap'}
                    >
                      Roadmap
                    </button>
                  </div>

                {canManage && workstreamSubTab === 'list' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingPhase(null);
                      setPhaseForm({ name: '', startDate: '', dueDate: '' });
                      setShowPhaseModal(true);
                    }}
                  >
                    <Plus size={16} /> Agregar Fase
                  </button>
                )}

                {(workstreamSubTab === 'roadmap' || workstreamSubTab === 'list') && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      if (workstreamSubTab === 'list') {
                        setShowListPreview(true);
                      } else {
                        setShowRoadmapPreview(true);
                      }
                    }}
                  >
                    <Eye size={16} /> Previsualizar / Exportar
                  </button>
                )}
                </div>
              </div>
            </div>

            {workstreamSubTab === 'list' ? (
              <div className="phases-list">
                {(() => {
                  const sortedPhases = [...(project.phases || [])].sort((a, b) => {
                    if (!a.startDate) return 1;
                    if (!b.startDate) return -1;
                    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
                  });

                  const getTaskStatusStyle = (status: string) => {
                    const styles: Record<string, { bg: string; color: string; border: string }> = {
                      'Todo': { bg: 'rgba(100, 116, 139, 0.1)', color: '#64748b', border: 'rgba(100, 116, 139, 0.2)' },
                      'In Progress': { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', border: 'rgba(59, 130, 246, 0.2)' },
                      'Review': { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', border: 'rgba(245, 158, 11, 0.2)' },
                      'Done': { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', border: 'rgba(16, 185, 129, 0.2)' }
                    };
                    return styles[status] || styles['Todo'];
                  };

                  const handleAddTaskToPhase = (phaseId: string) => {
                    setEditingTask(null);
                    setTaskForm({ name: '', description: '', assignedToId: '', status: 'Todo', dueDate: '', phaseId, taskType: 'Tarea', externalManager: '' });
                    setShowTaskModal(true);
                  };

                  const togglePhaseCollapse = (phaseId: string) => {
                    setCollapsedPhaseIds(prev =>
                      prev.includes(phaseId) ? prev.filter(id => id !== phaseId) : [...prev, phaseId]
                    );
                  };

                  return (
                    <>
                      {sortedPhases.map((phase: any) => {
                        const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
                        const total = phaseTasks.length;
                        const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
                        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
                        const isCollapsed = collapsedPhaseIds.includes(phase.id);
                        const isDraggedOver = draggedOverPhase === phase.id;

                        return (
                          <div
                            key={phase.id}
                            className={`phase-card ${isDraggedOver ? 'dragged-over' : ''}`}
                            onDragOver={(e) => {
                              if (canManage) {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                              }
                            }}
                            onDragEnter={() => { if (canManage) setDraggedOverPhase(phase.id); }}
                            onDragLeave={() => { if (canManage) setDraggedOverPhase(null); }}
                            onDrop={async (e) => {
                              if (!canManage) return;
                              e.preventDefault();
                              setDraggedOverPhase(null);
                              const taskId = e.dataTransfer.getData('text/plain');
                              const task = project.tasks?.find((t: any) => t.id === taskId);
                              if (task && task.phaseId !== phase.id) {
                                await updateTask({
                                  variables: {
                                    projectId: id,
                                    taskId: task.id,
                                    input: {
                                      name: task.name,
                                      description: task.description || '',
                                      assignedToId: task.assignedTo?.id || null,
                                      status: task.status,
                                      dueDate: task.dueDate,
                                      phaseId: phase.id,
                                      taskType: task.taskType || 'Tarea',
                                      externalManager: task.externalManager || ''
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <div className="phase-card-header">
                              <div className="phase-title-row">
                                <h3 className="phase-name">
                                  {phase.name}
                                </h3>
                                <div className="phase-header-actions" onClick={(e) => e.stopPropagation()}>
                                  {canManage && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-secondary btn-compact"
                                        onClick={() => handleAddTaskToPhase(phase.id)}
                                      >
                                        <Plus size={12} /> Tarea
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-icon"
                                        onClick={() => handleEditPhase(phase)}
                                        title="Editar Fase"
                                      >
                                        <Edit size={14} />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn-icon"
                                        onClick={() => handleDeletePhase(phase.id)}
                                        title="Eliminar Fase"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="phase-date-badge">
                                <Calendar size={12} />
                                <span>{formatCalendarDate(phase.startDate, true, 'es')} - {formatCalendarDate(phase.dueDate, true, 'es')}</span>
                              </div>

                              <div className="phase-progress-summary">
                                <div className="phase-progress-copy">
                                  <span>{completed} de {total} tareas completadas</span>
                                  <div className="phase-progress-bar-container">
                                    <div className="phase-progress-bar-fill" style={{ width: `${pct}%` }} />
                                  </div>
                                  <strong>{pct}%</strong>
                                </div>
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => togglePhaseCollapse(phase.id)}
                                  title={isCollapsed ? "Expandir Fase" : "Colapsar Fase"}
                                >
                                  {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                </button>
                              </div>
                            </div>

                            {!isCollapsed && (
                              <div className="phase-card-body">
                                <div className="phase-tasks-list">
                                  {phaseTasks.map((task: any) => {
                                    const statusStyle = getTaskStatusStyle(task.status);
                                    return (
                                      <div
                                        key={task.id}
                                        className="phase-task-row"
                                        onClick={() => handleEditTask(task)}
                                        draggable={canManage}
                                        onDragStart={(e) => {
                                          if (canManage) {
                                            e.dataTransfer.setData('text/plain', task.id);
                                            e.dataTransfer.effectAllowed = 'move';
                                          }
                                        }}
                                        onDragOver={(e) => {
                                          if (canManage) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.dataTransfer.dropEffect = 'move';
                                          }
                                        }}
                                        onDragEnter={(e) => {
                                          if (canManage) {
                                            e.stopPropagation();
                                            setDraggedOverTaskId(task.id);
                                          }
                                        }}
                                        onDragLeave={(e) => {
                                          if (canManage) {
                                            e.stopPropagation();
                                            setDraggedOverTaskId(null);
                                          }
                                        }}
                                        onDrop={async (e) => {
                                          if (!canManage) return;
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setDraggedOverTaskId(null);
                                          
                                          const draggedTaskId = e.dataTransfer.getData('text/plain');
                                          if (draggedTaskId === task.id) return;
                                          
                                          const taskA = project.tasks?.find((t: any) => t.id === draggedTaskId);
                                          if (!taskA) return;
                                          
                                          const otherTasks = (project.tasks || []).filter((t: any) => t.id !== draggedTaskId);
                                          const targetIndex = otherTasks.findIndex((t: any) => t.id === task.id);
                                          if (targetIndex === -1) return;
                                          
                                          const orderedIds = [];
                                          for (let i = 0; i < otherTasks.length; i++) {
                                            if (i === targetIndex) {
                                              orderedIds.push(draggedTaskId);
                                            }
                                            orderedIds.push(otherTasks[i].id);
                                          }
                                          if (!orderedIds.includes(draggedTaskId)) {
                                            orderedIds.push(draggedTaskId);
                                          }
                                          
                                          if (taskA.phaseId !== task.phaseId) {
                                            await updateTask({
                                              variables: {
                                                projectId: id,
                                                taskId: draggedTaskId,
                                                input: {
                                                  name: taskA.name,
                                                  description: taskA.description || '',
                                                  assignedToId: taskA.assignedTo?.id || null,
                                                  status: taskA.status,
                                                  dueDate: taskA.dueDate,
                                                  phaseId: task.phaseId,
                                                  taskType: taskA.taskType || 'Tarea',
                                                  externalManager: taskA.externalManager || ''
                                                }
                                              }
                                            });
                                          }
                                          
                                          await reorderTasks({
                                            variables: {
                                              projectId: id,
                                              taskIdsOrder: orderedIds
                                            }
                                          });
                                        }}
                                      >
                                        <div className="phase-task-left">
                                          <span className="phase-task-title">{task.name}</span>
                                          {task.description && (
                                            <span className="phase-task-description" title={task.description}>
                                              · {task.description}
                                            </span>
                                          )}
                                        </div>
                                        <div className="phase-task-right">
                                          <div className="phase-task-meta">
                                            <span
                                              className="badge"
                                              style={{
                                                backgroundColor: statusStyle.bg,
                                                color: statusStyle.color,
                                                border: `1px solid ${statusStyle.border}`,
                                                fontSize: '11px',
                                                padding: '2px 8px',
                                                borderRadius: '4px'
                                              }}
                                            >
                                              {task.status}
                                            </span>

                                            {task.dueDate && (
                                              <span className="phase-task-due-date">
                                                <Calendar size={11} />
                                                {formatCalendarDate(task.dueDate, false, 'es')}
                                              </span>
                                            )}

                                            <div className="phase-task-assignee">
                                              {task.taskType === 'Insumo' ? (
                                                <div className="phase-task-assignee">
                                                  <span className="badge" style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.2)', fontSize: '10px', padding: '1px 5px', borderRadius: '4px' }}>
                                                    Insumo
                                                  </span>
                                                  <span className="phase-task-assignee-name phase-task-assignee-name-muted" title={task.externalManager}>
                                                    {task.externalManager || 'Cliente'}
                                                  </span>
                                                </div>
                                              ) : task.assignedTo ? (
                                                <>
                                                  <div
                                                    className="avatar avatar-sm"
                                                    style={{
                                                      background: getAvatarColor(task.assignedTo.name),
                                                      width: '20px',
                                                      height: '20px',
                                                      fontSize: '9px',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      borderRadius: '50%',
                                                      color: '#fff',
                                                      fontWeight: 'bold'
                                                    }}
                                                    title={task.assignedTo.name}
                                                  >
                                                    {getInitials(task.assignedTo.name)}
                                                  </div>
                                                  <span className="phase-task-assignee-name" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                    {task.assignedTo.name.split(' ')[0]}
                                                  </span>
                                                </>
                                              ) : (
                                                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Sin Asignar</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {phaseTasks.length === 0 && (
                                    <div className="phase-empty-state">
                                      <Clock size={20} />
                                      <p style={{ margin: 0 }}>No hay tareas asignadas a esta fase.</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {sortedPhases.length === 0 && (
                        <div className="workstream-empty-state">
                          <Layers size={36} />
                          <h3>Sin fases definidas</h3>
                          <p>
                            Crea fases o etapas cronológicas (ej. Fase de Diseño, Desarrollo MVP, Testing) y clasifica tus tareas.
                          </p>
                        </div>
                      )}

                      {/* Unassigned Tasks Section as Drop Zone */}
                      {(() => {
                        const phaseIds = new Set((project.phases || []).map((p: any) => p.id));
                        const unassignedTasks = project.tasks?.filter((t: any) => !t.phaseId || !phaseIds.has(t.phaseId)) || [];
                        const isUnassignedDraggedOver = draggedOverPhase === 'unassigned';

                        return (
                          <div
                            className={`unassigned-section ${isUnassignedDraggedOver ? 'dragged-over' : ''}`}
                            onDragOver={(e) => {
                              if (canManage) {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                              }
                            }}
                            onDragEnter={() => { if (canManage) setDraggedOverPhase('unassigned'); }}
                            onDragLeave={() => { if (canManage) setDraggedOverPhase(null); }}
                            onDrop={async (e) => {
                              if (!canManage) return;
                              e.preventDefault();
                              setDraggedOverPhase(null);
                              const taskId = e.dataTransfer.getData('text/plain');
                              const task = project.tasks?.find((t: any) => t.id === taskId);
                              if (task && task.phaseId !== null) {
                                await updateTask({
                                  variables: {
                                    projectId: id,
                                    taskId: task.id,
                                    input: {
                                      name: task.name,
                                      description: task.description || '',
                                      assignedToId: task.assignedTo?.id || null,
                                      status: task.status,
                                      dueDate: task.dueDate,
                                      phaseId: null,
                                      taskType: task.taskType || 'Tarea',
                                      externalManager: task.externalManager || ''
                                    }
                                  }
                                });
                              }
                            }}
                          >
                            <div className="unassigned-section-header">
                              <h3>Tareas sin fase asignada ({unassignedTasks.length})</h3>
                              <p>Estas tareas pertenecen al proyecto pero no están vinculadas a una etapa cronológica. Arrástralas a una fase para calendarizarlas.</p>
                            </div>

                            {unassignedTasks.length > 0 ? (
                              <div className="phase-tasks-list">
                                {unassignedTasks.map((task: any) => {
                                  const statusStyle = getTaskStatusStyle(task.status);
                                  return (
                                    <div
                                      key={task.id}
                                      className="phase-task-row"
                                      onClick={() => handleEditTask(task)}
                                      draggable={canManage}
                                      onDragStart={(e) => {
                                        if (canManage) {
                                          e.dataTransfer.setData('text/plain', task.id);
                                          e.dataTransfer.effectAllowed = 'move';
                                        }
                                      }}
                                    >
                                      <div className="phase-task-left">
                                        <span className="phase-task-title">{task.name}</span>
                                        {task.description && (
                                          <span className="phase-task-description" title={task.description}>
                                            · {task.description}
                                          </span>
                                        )}
                                      </div>
                                      <div className="phase-task-right">
                                        <div className="phase-task-meta">
                                          <span
                                            className="badge"
                                            style={{
                                              backgroundColor: statusStyle.bg,
                                              color: statusStyle.color,
                                              border: `1px solid ${statusStyle.border}`,
                                              fontSize: '11px',
                                              padding: '2px 8px',
                                              borderRadius: '4px'
                                            }}
                                          >
                                            {task.status}
                                          </span>

                                          {task.dueDate && (
                                            <span className="phase-task-due-date">
                                              <Calendar size={11} />
                                              {formatCalendarDate(task.dueDate, false, 'es')}
                                            </span>
                                          )}

                                          <div className="phase-task-assignee">
                                            {task.taskType === 'Insumo' ? (
                                              <div className="phase-task-assignee">
                                                <span className="badge" style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.2)', fontSize: '10px', padding: '1px 5px', borderRadius: '4px' }}>
                                                  Insumo
                                                </span>
                                                <span className="phase-task-assignee-name phase-task-assignee-name-muted" title={task.externalManager}>
                                                  {task.externalManager || 'Cliente'}
                                                </span>
                                              </div>
                                            ) : task.assignedTo ? (
                                              <>
                                                <div
                                                  className="avatar avatar-sm"
                                                  style={{
                                                    background: getAvatarColor(task.assignedTo.name),
                                                    width: '20px',
                                                    height: '20px',
                                                    fontSize: '9px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '50%',
                                                    color: '#fff',
                                                    fontWeight: 'bold'
                                                  }}
                                                  title={task.assignedTo.name}
                                                >
                                                  {getInitials(task.assignedTo.name)}
                                                </div>
                                                <span className="phase-task-assignee-name">
                                                  {task.assignedTo.name.split(' ')[0]}
                                                </span>
                                              </>
                                            ) : (
                                              <span className="phase-task-assignee-empty">Sin Asignar</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="phase-empty-state compact">
                                Todas las tareas del proyecto están asignadas a una fase.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>
            ) : (
              /* Roadmap Gantt View */
              <div className="workstream-roadmap-view">
                {(() => {
                  const sortedPhases = [...(project.phases || [])].sort((a, b) => {
                    if (!a.startDate) return 1;
                    if (!b.startDate) return -1;
                    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
                  });

                  if (sortedPhases.length === 0) {
                    return (
                      <div className="workstream-empty-state">
                        <Calendar size={36} />
                        <h3>Sin fases definidas para generar el Roadmap</h3>
                        <p>Regresa a Lista y agrega al menos una fase con fechas de inicio y fin.</p>
                      </div>
                    );
                  }

                  let timelineStart = project.startDate ? new Date(project.startDate) : new Date();
                  let timelineEnd = project.dueDate ? new Date(project.dueDate) : new Date();

                  sortedPhases.forEach(p => {
                    if (p.startDate) {
                      const d = new Date(p.startDate);
                      if (d < timelineStart) timelineStart = d;
                    }
                    if (p.dueDate) {
                      const d = new Date(p.dueDate);
                      if (d > timelineEnd) timelineEnd = d;
                    }
                  });

                  timelineStart = new Date(timelineStart.getTime() - 7 * 24 * 60 * 60 * 1000);
                  timelineEnd = new Date(timelineEnd.getTime() + 7 * 24 * 60 * 60 * 1000);

                  const totalDays = Math.ceil((timelineEnd.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));

                  const getPhaseTimelineProps = (phase: any) => {
                    if (!phase.startDate || !phase.dueDate) return null;
                    const pStart = new Date(phase.startDate);
                    const pEnd = new Date(phase.dueDate);
                    const offset = Math.max(0, Math.ceil((pStart.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
                    const duration = Math.max(1, Math.ceil((pEnd.getTime() - pStart.getTime()) / (1000 * 60 * 60 * 24)));
                    return {
                      left: (offset / totalDays) * 100,
                      width: (duration / totalDays) * 100
                    };
                  };

                  const getTaskTimelineProps = (phase: any, task: any) => {
                    if (!task.dueDate) return null;
                    const tEnd = new Date(task.dueDate);
                    const tStart = phase.startDate ? new Date(phase.startDate) : new Date(timelineStart);
                    const startVal = tStart < timelineStart ? timelineStart : tStart;
                    const endVal = tEnd > timelineEnd ? timelineEnd : (tEnd < startVal ? startVal : tEnd);
                    
                    const offset = Math.max(0, Math.ceil((startVal.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
                    const duration = Math.max(1, Math.ceil((endVal.getTime() - startVal.getTime()) / (1000 * 60 * 60 * 24)));
                    return {
                      left: (offset / totalDays) * 100,
                      width: (duration / totalDays) * 100
                    };
                  };

                  const getTaskStatusColor = (status: string) => {
                    switch (status) {
                      case 'Todo': return '#64748b';
                      case 'In Progress': return '#f59e0b';
                      case 'Review': return '#8b5cf6';
                      case 'Done': return '#10b981';
                      default: return '#64748b';
                    }
                  };

                  const getMonthMarkers = () => {
                    const markers = [];
                    const current = new Date(timelineStart);
                    while (current <= timelineEnd) {
                      markers.push(new Date(current));
                      current.setMonth(current.getMonth() + 1);
                    }
                    return markers;
                  };
                  const monthMarkers = getMonthMarkers();

                  return (
                    <div id="roadmap-container" className="workstream-roadmap-grid">
                      <div className="workstream-roadmap-header">
                        <div className="workstream-roadmap-left-header" style={{ width: `${leftColWidth}px` }}>Fase / Etapa</div>
                        <div className="workstream-roadmap-months">
                          {monthMarkers.map((markerDate, idx) => {
                            const timeDiff = markerDate.getTime() - timelineStart.getTime();
                            const left = (timeDiff / (1000 * 60 * 60 * 24) / totalDays) * 100;
                            return (
                              <span
                                key={idx}
                                className="workstream-roadmap-month"
                                style={{
                                  left: `${left}%`,
                                }}
                              >
                                {markerDate.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }).toUpperCase()}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Resizer Handle */}
                      <div
                        onMouseDown={startResizing}
                        className={`workstream-roadmap-resizer ${isResizing ? 'active' : ''}`}
                        style={{
                          left: `${leftColWidth - 2}px`,
                        }}
                        title="Arrastra para cambiar el tamaño"
                      />

                      <div className="workstream-roadmap-rows">
                        <div className="workstream-roadmap-guides" style={{ left: `${leftColWidth}px` }}>
                          {monthMarkers.map((markerDate, idx) => {
                            const timeDiff = markerDate.getTime() - timelineStart.getTime();
                            const left = (timeDiff / (1000 * 60 * 60 * 24) / totalDays) * 100;
                            return (
                              <div
                                key={idx}
                                className="workstream-roadmap-guide"
                                style={{
                                  left: `${left}%`,
                                }}
                              />
                            );
                          })}
                        </div>

                        {sortedPhases.map((phase: any) => {
                          const props = getPhaseTimelineProps(phase);
                          const phaseTasks = project.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
                          const total = phaseTasks.length;
                          const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
                          const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

                          return (
                            <React.Fragment key={phase.id}>
                              <div className="workstream-roadmap-row phase-row">
                                <div className="workstream-roadmap-label" style={{ width: `${leftColWidth}px` }}>
                                  <div className="workstream-roadmap-label-title" title={phase.name}>
                                    {phase.name}
                                  </div>
                                  <div className="workstream-roadmap-label-meta">
                                    {formatCalendarDate(phase.startDate, false, 'es')} - {formatCalendarDate(phase.dueDate, false, 'es')}
                                  </div>
                                </div>

                                <div className="workstream-roadmap-track phase-track">
                                  {props && (
                                    <div
                                      className="gantt-bar-item phase-bar"
                                      style={{
                                        left: `${props.left}%`,
                                        width: `${props.width}%`,
                                      }}
                                    >
                                      {pct > 0 && (
                                        <div
                                          className="gantt-bar-fill"
                                          style={{
                                            width: `${pct}%`,
                                          }}
                                        />
                                      )}
                                      <span
                                        className="gantt-bar-percent"
                                        style={{
                                          color: pct > 45 ? '#ffffff' : 'var(--text-primary)',
                                        }}
                                      >
                                        {pct}%
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Task Rows */}
                              {phaseTasks.map((task: any) => {
                                const tProps = getTaskTimelineProps(phase, task);
                                const assigneeName = task.taskType === 'Insumo'
                                  ? (task.externalManager || 'N/A')
                                  : (task.assignedTo?.name || 'Sin Asignar');
                                return (
                                  <div key={task.id} className="workstream-roadmap-row task-row">
                                    <div className="workstream-roadmap-label task-label" style={{ width: `${leftColWidth - 12}px` }}>
                                      <div className="workstream-roadmap-label-title task-title" title={task.name}>
                                        ↳ {task.name}
                                      </div>
                                      <div className="workstream-roadmap-task-meta">
                                        <span style={{
                                          background: task.taskType === 'Insumo' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(79, 70, 229, 0.1)',
                                          color: task.taskType === 'Insumo' ? '#f59e0b' : 'var(--primary)',
                                          border: task.taskType === 'Insumo' ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(79, 70, 229, 0.2)'
                                        }}>
                                          {task.taskType || 'Tarea'}
                                        </span>
                                        <span title={`Asignado: ${assigneeName}`}>
                                          {assigneeName}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="workstream-roadmap-track task-track">
                                      {tProps && (
                                        <div
                                          className={`gantt-task-bar ${task.status.toLowerCase().replace(' ', '-')}`}
                                          style={{
                                            left: `${tProps.left}%`,
                                            width: `${tProps.width}%`,
                                            background: getTaskStatusColor(task.status),
                                          }}
                                          title={`${task.name} (${task.status}) - Vence: ${task.dueDate ? formatCalendarDate(task.dueDate, false, 'es') : 'N/A'}`}
                                        />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tasks' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Task Kanban Board</h2>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Manage your team task list and tracking.</p>
              </div>
              {canManage && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingTask(null);
                    setTaskForm({ name: '', description: '', assignedToId: '', status: 'Todo', dueDate: '', phaseId: '', taskType: 'Tarea', externalManager: '' });
                    setShowTaskModal(true);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Plus size={16} /> Add Task
                </button>
              )}
            </div>
 
            <div className="kanban-board">
              {['Todo', 'In Progress', 'Review', 'Done'].map(colStatus => {
                const colTasks = project.tasks?.filter((t: any) => t.status === colStatus) || [];
                return (
                  <div key={colStatus} className="kanban-column">
                    <div className="kanban-column-header">
                      <div className="kanban-column-title">
                        {colStatus === 'Todo' ? 'To Do' : colStatus}
                      </div>
                      <span className="kanban-column-count">{colTasks.length}</span>
                    </div>
 
                    <div
                      onDragOver={(e) => {
                        if (canWriteCommentsTasks) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }
                      }}
                      onDragEnter={() => { if (canWriteCommentsTasks) setDraggedOverCol(colStatus); }}
                      onDragLeave={() => { if (canWriteCommentsTasks) setDraggedOverCol(null); }}
                      onDrop={async (e) => {
                        if (!canWriteCommentsTasks) return;
                        e.preventDefault();
                        setDraggedOverCol(null);
                        const taskId = e.dataTransfer.getData('text/plain');
                        const task = project.tasks?.find((t: any) => t.id === taskId);
                        if (task && task.status !== colStatus) {
                          await updateTask({
                            variables: {
                              projectId: id,
                              taskId: task.id,
                              input: {
                                name: task.name,
                                description: task.description || '',
                                assignedToId: task.assignedTo?.id || null,
                                status: colStatus,
                                dueDate: task.dueDate,
                                phaseId: task.phaseId || null,
                                taskType: task.taskType || 'Tarea',
                                externalManager: task.externalManager || ''
                              }
                            }
                          });
                        }
                      }}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        flex: 1,
                        minHeight: '150px',
                        padding: '8px',
                        borderRadius: '8px',
                        border: draggedOverCol === colStatus ? '2px dashed var(--primary)' : '2px solid transparent',
                        background: draggedOverCol === colStatus ? 'rgba(79, 70, 229, 0.02)' : 'transparent',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {colTasks.map((task: any) => (
                        <div
                          key={task.id}
                          className="kanban-card"
                          onClick={() => handleEditTask(task)}
                          draggable={canWriteCommentsTasks}
                          onDragStart={(e) => {
                            if (canWriteCommentsTasks) {
                              e.dataTransfer.setData('text/plain', task.id);
                              e.dataTransfer.effectAllowed = 'move';
                            }
                          }}
                          style={{ cursor: canWriteCommentsTasks ? 'grab' : 'pointer' }}
                        >
                          <div className="kanban-card-title">{task.name}</div>
                          
                          <div className="kanban-card-meta">
                            <div className="kanban-card-assignee">
                              {task.taskType === 'Insumo' ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                  <span className="badge" style={{ backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', border: '1px solid rgba(245, 158, 11, 0.2)', fontSize: '9px', padding: '1px 4px', borderRadius: '3px' }}>
                                    Insumo
                                  </span>
                                  <span style={{ fontSize: '11px', fontStyle: 'italic', color: 'var(--text-secondary)' }} title={task.externalManager}>
                                    {task.externalManager || 'Cliente'}
                                  </span>
                                </div>
                              ) : task.assignedTo ? (
                                <>
                                  <div
                                    className="avatar avatar-sm"
                                    style={{ 
                                      background: getAvatarColor(task.assignedTo.name),
                                      width: '20px',
                                      height: '20px',
                                      fontSize: '9px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    title={task.assignedTo.name}
                                  >
                                    {getInitials(task.assignedTo.name)}
                                  </div>
                                  <span style={{ fontSize: '11px' }}>{task.assignedTo.name.split(' ')[0]}</span>
                                </>
                              ) : (
                                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Unassigned</span>
                              )}
                            </div>

                            {task.dueDate && (
                              <div className="kanban-card-date">
                                <Calendar size={10} />
                                {formatCalendarDate(task.dueDate, false, 'en')}
                              </div>
                            )}
                          </div>

                          <div className="kanban-card-actions" onClick={(e) => e.stopPropagation()}>
                            {canWriteCommentsTasks && colStatus !== 'Todo' && (
                              <button
                                type="button"
                                className="btn-icon"
                                style={{ padding: '4px', fontSize: '14px', fontWeight: 'bold' }}
                                title="Move to previous"
                                onClick={() => {
                                  const statuses = ['Todo', 'In Progress', 'Review', 'Done'];
                                  const prevIdx = statuses.indexOf(colStatus) - 1;
                                  handleQuickMoveTask(task, statuses[prevIdx]);
                                }}
                              >
                                  ‹
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn-icon"
                              style={{ padding: '4px' }}
                              title="Edit"
                              onClick={() => handleEditTask(task)}
                            >
                              <Edit2 size={12} />
                            </button>
                            {canManage && (
                              <button
                                type="button"
                                className="btn-icon text-danger"
                                style={{ padding: '4px' }}
                                title="Delete"
                                onClick={() => handleDeleteTask(task.id)}
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                            {canWriteCommentsTasks && colStatus !== 'Done' && (
                              <button
                                type="button"
                                className="btn-icon"
                                style={{ padding: '4px', fontSize: '14px', fontWeight: 'bold' }}
                                title="Move to next"
                                onClick={() => {
                                  const statuses = ['Todo', 'In Progress', 'Review', 'Done'];
                                  const nextIdx = statuses.indexOf(colStatus) + 1;
                                  handleQuickMoveTask(task, statuses[nextIdx]);
                                }}
                              >
                                ›
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {colTasks.length === 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px', border: '1px dashed var(--border-hover)', borderRadius: '8px', color: 'var(--text-muted)', fontSize: '12px' }}>
                          Empty
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Project Notes</h2>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Add comments, details, or documentation links.</p>
              </div>
              {canManage && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingNote(null);
                    setNoteForm({ title: '', content: '' });
                    setShowNoteModal(true);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Plus size={16} /> New Note
                </button>
              )}
            </div>

            <div className="notes-grid">
              {project.notes?.map((note: any) => (
                <div key={note.id} className="note-card">
                  <div className="note-card-title">{note.title}</div>
                  <div className="note-card-content">{note.content}</div>
                  
                  <div className="note-card-footer">
                    <span>{new Date(note.createdAt || note.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    {canManage && (
                      <div className="note-card-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleEditNote(note)}
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon text-danger"
                          onClick={() => handleDeleteNote(note.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(!project.notes || project.notes.length === 0) && (
                <div className="detail-card" style={{ gridColumn: '1 / -1', padding: '32px', textAlign: 'center' }}>
                  <span style={{ color: 'var(--text-muted)' }}>No notes added yet. Click "New Note" to add one.</span>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'assets-files' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 700 }}>Project Assets</h2>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Upload and view images, PDFs, Excel sheets, and documents.</p>
              </div>
            </div>

            {/* Upload Area */}
            {canManage && (
              <label className="upload-dropzone">
                <input
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <div className="upload-icon">
                  <Paperclip size={32} />
                </div>
                <div className="upload-text">
                  <strong>Click to upload</strong> or drag and drop files here
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                    PDF, Images, Excel, Word (Max 10MB)
                  </div>
                </div>
              </label>
            )}

            <div className="assets-grid">
              {project.assets?.map((asset: any) => (
                <div key={asset.id} className="asset-card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className={`asset-icon-wrapper ${asset.fileType || 'other'}`}>
                      {getAssetIcon(asset.fileType)}
                    </div>
                    <div className="asset-details">
                      <div className="asset-name" title={asset.name}>{asset.name}</div>
                      <div className="asset-meta">{asset.fileSize}</div>
                    </div>
                  </div>
                  
                  <div className="asset-footer">
                    <span className="asset-date">
                      {new Date(asset.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => setPreviewAsset(asset)}
                        title="Ver / Previsualizar"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <Eye size={14} />
                      </button>
                      <a
                        href={asset.url}
                        download={asset.name}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-icon"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                        title="Descargar"
                      >
                        <Download size={14} />
                      </a>
                      {canManage && (
                        <button
                          type="button"
                          className="btn-icon text-danger"
                          onClick={() => handleDeleteAsset(asset.id)}
                          title="Eliminar"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {(!project.assets || project.assets.length === 0) && (
                <div className="detail-card" style={{ gridColumn: '1 / -1', padding: '32px', textAlign: 'center' }}>
                  <span style={{ color: 'var(--text-muted)' }}>No files uploaded yet. Select files above to upload them.</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div >

      {/* Edit Modal */}
      {
        showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
              <div className="modal-header">
                <h2>Edit Project</h2>
                <button className="btn-icon" onClick={() => setShowEditModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleUpdate}>
                {/* Modal Tab Selectors */}
                <div className="tabs" style={{ marginBottom: '24px', padding: '0 24px', borderBottom: '1px solid var(--border)' }}>
                  {['General', 'Scope & Features', 'Team'].map((tabName) => {
                    const tabId = tabName === 'General' ? 'general' : (tabName === 'Scope & Features' ? 'scope' : 'team');
                    return (
                      <button
                        type="button"
                        key={tabId}
                        className={`tab ${modalTab === tabId ? 'active' : ''}`}
                        onClick={() => setModalTab(tabId)}
                        style={{ paddingBottom: '12px' }}
                      >
                        {tabName}
                      </button>
                    );
                  })}
                </div>

                <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto', padding: '0 24px 24px' }}>
                  
                  {modalTab === 'general' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">Project Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-textarea"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                      </div>

                      <div className="form-group">
                        <label className="checkbox-item" style={{ display: 'inline-flex', gap: '8px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.followUp}
                            onChange={(e) => setFormData({ ...formData, followUp: e.target.checked })}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span style={{ fontSize: '14px', fontWeight: 500 }}>Follow Up Project</span>
                        </label>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Client</label>
                          <select
                            className="form-select"
                            value={formData.clientId}
                            onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                          >
                            <option value="">Select client</option>
                            {clientsData?.clients?.clients?.map((client: Client) => (
                              <option key={client.id} value={client.id}>{client.company}</option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Type</label>
                          <select
                            className="form-select"
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                          >
                            <option value="">Select type</option>
                            {typesData?.catalogs?.map((type: Catalog) => (
                              <option key={type.id} value={type.name}>{type.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          >
                            {statusesData?.catalogs?.map((status: Catalog) => (
                              <option key={status.id} value={status.name}>{status.name}</option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Priority</label>
                          <select
                            className="form-select"
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                          >
                            {prioritiesData?.catalogs?.map((priority: Catalog) => (
                              <option key={priority.id} value={priority.name}>{priority.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Size</label>
                          <select
                            className="form-select"
                            value={formData.size}
                            onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                          >
                            <option value="">Select size</option>
                            {sizesData?.catalogs?.map((item: Catalog) => (
                              <option key={item.id} value={item.name}>{item.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Start Date</label>
                          <input
                            type="date"
                            className="form-input"
                            value={formData.startDate}
                            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Due Date</label>
                          <input
                            type="date"
                            className="form-input"
                            value={formData.dueDate}
                            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">Region</label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.region}
                            onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Group</label>
                          <input
                            type="text"
                            className="form-input"
                            value={formData.groupName}
                            onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {modalTab === 'scope' && (
                    <>
                      <div className="form-group">
                        <label className="form-label">In Scope (one item per line)</label>
                        <textarea
                          className="form-textarea"
                          value={formData.inScope}
                          onChange={(e) => setFormData({ ...formData, inScope: e.target.value })}
                          rows={4}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Out of Scope (one item per line)</label>
                        <textarea
                          className="form-textarea"
                          value={formData.outOfScope}
                          onChange={(e) => setFormData({ ...formData, outOfScope: e.target.value })}
                          rows={4}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Expected Outcomes (one item per line)</label>
                        <textarea
                          className="form-textarea"
                          value={formData.expectedOutcomes}
                          onChange={(e) => setFormData({ ...formData, expectedOutcomes: e.target.value })}
                          rows={4}
                        />
                      </div>

                      <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                        <div className="form-group">
                          <label className="form-label">P0 Features</label>
                          <textarea
                            className="form-textarea"
                            value={formData.keyFeaturesP0}
                            onChange={(e) => setFormData({ ...formData, keyFeaturesP0: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">P1 Features</label>
                          <textarea
                            className="form-textarea"
                            value={formData.keyFeaturesP1}
                            onChange={(e) => setFormData({ ...formData, keyFeaturesP1: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">P2 Features</label>
                          <textarea
                            className="form-textarea"
                            value={formData.keyFeaturesP2}
                            onChange={(e) => setFormData({ ...formData, keyFeaturesP2: e.target.value })}
                            rows={3}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {modalTab === 'team' && (
                    <div className="team-assign-container">
                      <div className="team-assign-search-wrapper" style={{ position: 'relative', marginBottom: '8px' }}>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Search developers by name or skill..."
                          value={teamSearch}
                          onChange={(e) => setTeamSearch(e.target.value)}
                          style={{ width: '100%', paddingLeft: '14px' }}
                        />
                      </div>

                      {/* Filter Chips */}
                      <div className="team-filter-chips">
                        <button
                          type="button"
                          className={`team-filter-chip ${activeFilter === 'all' ? 'active' : ''}`}
                          onClick={() => setActiveFilter('all')}
                        >
                          Todos ({resourcesData?.resources?.resources?.length || 0})
                        </button>
                        <button
                          type="button"
                          className={`team-filter-chip ${activeFilter === 'assigned' ? 'active' : ''}`}
                          onClick={() => setActiveFilter('assigned')}
                        >
                          Asignados ({selectedDevelopers.length})
                        </button>
                        <button
                          type="button"
                          className={`team-filter-chip ${activeFilter === 'available' ? 'active' : ''}`}
                          onClick={() => setActiveFilter('available')}
                        >
                          Disponibles
                        </button>
                        <button
                          type="button"
                          className={`team-filter-chip ${activeFilter === 'overloaded' ? 'active' : ''}`}
                          onClick={() => setActiveFilter('overloaded')}
                        >
                          Sobrecargados
                        </button>
                        
                        <div style={{ width: '100%', height: '1px', background: 'var(--border)', margin: '4px 0' }} />
                        
                        <button
                          type="button"
                          className={`team-filter-chip ${levelFilter === 'all' ? 'active' : ''}`}
                          onClick={() => setLevelFilter('all')}
                        >
                          Todos los niveles
                        </button>
                        <button
                          type="button"
                          className={`team-filter-chip ${levelFilter === 'Junior' ? 'active' : ''}`}
                          onClick={() => setLevelFilter('Junior')}
                        >
                          Junior
                        </button>
                        <button
                          type="button"
                          className={`team-filter-chip ${levelFilter === 'Mid' ? 'active' : ''}`}
                          onClick={() => setLevelFilter('Mid')}
                        >
                          Mid
                        </button>
                        <button
                          type="button"
                          className={`team-filter-chip ${levelFilter === 'Senior' ? 'active' : ''}`}
                          onClick={() => setLevelFilter('Senior')}
                        >
                          Senior
                        </button>
                      </div>

                      <div className="team-assign-list">
                        {resourcesData?.resources?.resources?.filter((resource: Resource) => {
                          const matchesSearch = resource.name.toLowerCase().includes(teamSearch.toLowerCase()) ||
                            (resource.skill && resource.skill.toLowerCase().includes(teamSearch.toLowerCase()));
                          
                          if (!matchesSearch) return false;

                          const isAssigned = selectedDevelopers.some(d => d.resourceId === resource.id);
                          const currentSize = formData.size || project?.size || 'M';
                          const weight = getProjectWeight(currentSize, resource.level);
                          const isCurrentlyAssigned = project?.developers?.some(d => d.resource?.id === resource.id) || false;
                          const load = resource.load !== undefined ? parseFloat(String(resource.load)) : 0;
                          const baseLoad = isCurrentlyAssigned ? load - weight : load;
                          const newLoad = isAssigned ? baseLoad + weight : baseLoad;

                          if (activeFilter === 'assigned' && !isAssigned) return false;
                          if (activeFilter === 'available' && newLoad > 100) return false;
                          if (activeFilter === 'overloaded' && newLoad <= 100) return false;

                          if (levelFilter !== 'all') {
                            let resNorm = 'Senior';
                            const level = resource.level || '';
                            if (level === 'Junior' || level === 'Junior 1' || level === 'Junior 2' || level === 'Junior 3' || level.startsWith('Junior ')) {
                              resNorm = 'Junior';
                            } else if (level === 'Mid' || level === 'Mid 1' || level === 'Mid 2' || level.startsWith('Mid ')) {
                              resNorm = 'Mid';
                            }
                            if (resNorm !== levelFilter) return false;
                          }

                          return true;
                        }).map((resource: Resource) => {
                          const isAssigned = selectedDevelopers.some(d => d.resourceId === resource.id);
                          const currentSize = formData.size || project?.size || 'M';
                          const weight = getProjectWeight(currentSize, resource.level);
                          const isCurrentlyAssigned = project?.developers?.some(d => d.resource?.id === resource.id) || false;
                          const load = resource.load !== undefined ? parseFloat(String(resource.load)) : 0;
                          const baseLoad = isCurrentlyAssigned ? load - weight : load;
                          const newLoad = isAssigned ? baseLoad + weight : baseLoad;
                          const isOverloaded = newLoad > 100;
                          const isBlocked = baseLoad > 100 && !isAssigned;
                          const loadPct = parseFloat((isAssigned ? newLoad : baseLoad).toFixed(2));
                          const devEntry = selectedDevelopers.find(d => d.resourceId === resource.id);
                          const hasMissingReason = isAssigned && isOverloaded && (!devEntry?.overloadReason || !devEntry.overloadReason.trim());

                          return (
                            <div
                              key={resource.id}
                              className={`team-assign-card ${isAssigned ? 'selected' : ''} ${isBlocked ? 'disabled' : ''} ${hasMissingReason ? 'invalid-reason' : ''}`}
                              onClick={() => { if (!isBlocked) toggleDeveloper(resource); }}
                            >
                              <div className="team-assign-row-main">
                                <div className="team-assign-left">
                                  <input
                                    type="checkbox"
                                    className="team-assign-checkbox"
                                    checked={isAssigned}
                                    disabled={isBlocked}
                                    onChange={(e) => { e.stopPropagation(); if (!isBlocked) toggleDeveloper(resource); }}
                                  />
                                  <div
                                    className="avatar avatar-sm"
                                    style={{ background: getAvatarColor(resource.name), width: '32px', height: '32px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  >
                                    {isBlocked ? <Lock size={14} style={{ color: '#fff' }} /> : getInitials(resource.name)}
                                  </div>
                                  <div className="team-assign-details">
                                    <span className="team-assign-name">
                                      {resource.name}
                                      {isBlocked && (
                                        <span style={{ fontSize: '10px', color: 'var(--danger-text)', background: 'var(--danger-light)', padding: '2px 6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                                          <Lock size={8} /> Bloqueado
                                        </span>
                                      )}
                                    </span>
                                    <span className="team-assign-meta">{resource.skill || 'Generalist'} • {resource.level} • Peso: +{weight}%</span>
                                  </div>
                                </div>

                                <div className="team-assign-mid" onClick={(e) => e.stopPropagation()}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    {isOverloaded ? (
                                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--danger-text)' }}>
                                        Sobrecargado ({loadPct}% carga)
                                      </span>
                                    ) : loadPct === 100 ? (
                                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--warning-text)' }}>
                                        Lleno (100% carga)
                                      </span>
                                    ) : (
                                      <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--success-text)' }}>
                                        {loadPct}% ocupado
                                      </span>
                                    )}
                                  </div>
                                  <div className="capacity-bar-container">
                                    <div
                                      className={`capacity-bar ${loadPct > 100 ? 'danger' : loadPct >= 100 ? 'warning' : 'success'}`}
                                      style={{ width: `${Math.min(100, Math.max(0, loadPct))}%` }}
                                    />
                                  </div>
                                </div>

                                <div className="team-assign-right" onClick={(e) => e.stopPropagation()}>
                                  {isAssigned && (
                                    <div className="role-toggle-group">
                                      <button
                                        type="button"
                                        className={`role-toggle-btn ${devEntry?.role !== 'lead' ? 'active' : ''}`}
                                        onClick={() => {
                                          setSelectedDevelopers(selectedDevelopers.map(d =>
                                            d.resourceId === resource.id ? { ...d, role: 'support' } : d
                                          ));
                                        }}
                                      >
                                        Support
                                      </button>
                                      <button
                                        type="button"
                                        className={`role-toggle-btn ${devEntry?.role === 'lead' ? 'active' : ''}`}
                                        onClick={() => {
                                          setSelectedDevelopers(selectedDevelopers.map(d =>
                                            d.resourceId === resource.id ? { ...d, role: 'lead' } : d
                                          ));
                                        }}
                                      >
                                        Lead
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {isAssigned && isOverloaded && (
                                <div className="overload-reason-field" onClick={(e) => e.stopPropagation()}>
                                  <label className="overload-reason-label">
                                    <AlertTriangle size={12} />
                                    Se requiere motivo de sobreasignación:
                                  </label>
                                  <input
                                    type="text"
                                    className={`overload-reason-input ${devEntry?.overloadReason?.trim() ? 'valid' : ''}`}
                                    placeholder="Ej: Requerido por experiencia en integraciones complejas..."
                                    value={devEntry?.overloadReason || ''}
                                    onChange={(e) => updateOverloadReason(resource.id, e.target.value)}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {resourcesData?.resources?.resources?.filter((resource: Resource) => {
                          const matchesSearch = resource.name.toLowerCase().includes(teamSearch.toLowerCase()) ||
                            (resource.skill && resource.skill.toLowerCase().includes(teamSearch.toLowerCase()));
                          
                          if (!matchesSearch) return false;

                          const isAssigned = selectedDevelopers.some(d => d.resourceId === resource.id);
                          const currentSize = formData.size || project?.size || 'M';
                          const weight = getProjectWeight(currentSize, resource.level);
                          const isCurrentlyAssigned = project?.developers?.some(d => d.resource?.id === resource.id) || false;
                          const load = resource.load !== undefined ? parseFloat(String(resource.load)) : 0;
                          const baseLoad = isCurrentlyAssigned ? load - weight : load;
                          const newLoad = isAssigned ? baseLoad + weight : baseLoad;

                          if (activeFilter === 'assigned' && !isAssigned) return false;
                          if (activeFilter === 'available' && newLoad > 100) return false;
                          if (activeFilter === 'overloaded' && newLoad <= 100) return false;

                          if (levelFilter !== 'all') {
                            let resNorm = 'Senior';
                            const level = resource.level || '';
                            if (level === 'Junior' || level === 'Junior 1' || level === 'Junior 2' || level === 'Junior 3' || level.startsWith('Junior ')) {
                              resNorm = 'Junior';
                            } else if (level === 'Mid' || level === 'Mid 1' || level === 'Mid 2' || level.startsWith('Mid ')) {
                              resNorm = 'Mid';
                            }
                            if (resNorm !== levelFilter) return false;
                          }

                          return true;
                        }).length === 0 && (
                          <div className="empty-state" style={{ padding: '24px' }}>
                            <span style={{ fontSize: '14px', color: 'var(--text-muted)' }}>No developers found</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                </div>
                <div className="modal-footer">
                  {hasInvalidOverloads() && (
                    <div className="validation-alert-footer">
                      <AlertTriangle size={14} />
                      <span>Se requiere el motivo de sobrecarga para los recursos asignados.</span>
                    </div>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={hasInvalidOverloads()}>
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }

      {/* Task Modal */}
      {showTaskModal && (
        <div className="modal-overlay" onClick={() => setShowTaskModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: editingTask ? '600px' : '450px' }}>
            <div className="modal-header">
              <h2>{editingTask ? 'Edit Task' : 'Add Task'}</h2>
              <button type="button" className="btn-icon" onClick={() => setShowTaskModal(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleTaskSubmit}>
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="form-group">
                  <label className="form-label">Task Name</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    placeholder="Enter task name..."
                    value={taskForm.name}
                    onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                    disabled={!canManage}
                  />
                </div>
                <div className="form-group task-description-field">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Add a detailed description for this task..."
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    rows={3}
                    disabled={!canManage}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Tipo de Tarea</label>
                  <select
                    className="form-input"
                    value={taskForm.taskType}
                    onChange={(e) => setTaskForm({ ...taskForm, taskType: e.target.value })}
                    disabled={!canManage}
                  >
                    <option value="Tarea">Tarea (Interno)</option>
                    <option value="Insumo">Insumo (Cliente/Externo)</option>
                  </select>
                </div>
                {taskForm.taskType === 'Tarea' ? (
                  <div className="form-group">
                    <label className="form-label">Assignee</label>
                    <select
                      className="form-input"
                      value={taskForm.assignedToId}
                      onChange={(e) => setTaskForm({ ...taskForm, assignedToId: e.target.value })}
                      disabled={!canManage}
                    >
                      <option value="">Unassigned</option>
                      {project.developers?.filter((dev: any) => dev && dev.resource).map((dev: any) => (
                        <option key={dev.resource.id} value={dev.resource.id}>
                          {dev.resource.name} ({dev.role === 'lead' ? 'Lead' : 'Support'})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="form-group">
                    <label className="form-label">Encargado del Insumo (Cliente) *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      placeholder="Nombre del encargado..."
                      value={taskForm.externalManager}
                      onChange={(e) => setTaskForm({ ...taskForm, externalManager: e.target.value })}
                      disabled={!canManage}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Phase / Etapa</label>
                  <select
                    className="form-input"
                    value={taskForm.phaseId}
                    onChange={(e) => setTaskForm({ ...taskForm, phaseId: e.target.value })}
                    disabled={!canManage}
                  >
                    <option value="">Sin Fase / General</option>
                    {project.phases?.map((phase: any) => (
                      <option key={phase.id} value={phase.id}>
                        {phase.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: editingTask ? '0' : '8px' }}>
                  <div>
                    <label className="form-label">Status</label>
                    <select
                      className="form-input"
                      value={taskForm.status}
                      onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                      disabled={!canWriteCommentsTasks}
                    >
                      <option value="Todo">Todo</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Review">Review</option>
                      <option value="Done">Done</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">Due Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={taskForm.dueDate}
                      onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                      disabled={!canManage}
                    />
                  </div>
                </div>

                {editingTask && (
                  <div className="task-comments-section">
                    <h3>
                      <MessageSquare size={15} /> Comentarios
                    </h3>
                    <div className="task-comments-list">
                      {(project.tasks?.find((t: any) => t.id === editingTask.id)?.comments || []).map((comment: any) => (
                        <div key={comment.id} className="comment-item">
                          <div
                            className="avatar avatar-sm comment-avatar"
                            style={{
                              background: getAvatarColor(comment.createdBy),
                              width: '24px',
                              height: '24px',
                              fontSize: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title={comment.createdBy}
                          >
                            {getInitials(comment.createdBy)}
                          </div>
                          <div className="comment-body">
                            <div className="comment-header">
                              <span className="comment-author">{comment.createdBy}</span>
                              <span className="comment-time">
                                {new Date(comment.createdAt).toLocaleString('es-ES', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                            <div className="comment-content">{comment.content}</div>
                          </div>
                          {canManage && (
                            <button
                              type="button"
                              className="comment-delete-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteComment(comment.id);
                              }}
                              title="Eliminar comentario"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      ))}
                      {(project.tasks?.find((t: any) => t.id === editingTask.id)?.comments || []).length === 0 && (
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '12px 0' }}>
                          No hay comentarios todavía. ¡Sé el primero en comentar!
                        </div>
                      )}
                    </div>
                    {canWriteCommentsTasks && (
                      <div className="comment-form">
                        <textarea
                          className="comment-form-input"
                          placeholder="Escribe un comentario..."
                          value={newCommentText}
                          onChange={(e) => setNewCommentText(e.target.value)}
                          rows={1}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAddComment(e);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleAddComment}
                          style={{ height: '38px', padding: '0 16px', fontSize: '13px' }}
                        >
                          Comentar
                        </button>
                      </div>
                    )}
                  </div>
                )}

              </div>
              <div className="modal-footer" style={{ padding: '16px 24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTaskModal(false)}>
                  {canWriteCommentsTasks ? 'Cancel' : 'Close'}
                </button>
                {canWriteCommentsTasks && (
                  <button type="submit" className="btn btn-primary">
                    {editingTask ? 'Save Changes' : 'Create Task'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Phase Modal */}
      {showPhaseModal && (
        <div className="modal-overlay" onClick={() => setShowPhaseModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>{editingPhase ? 'Editar Fase' : 'Añadir Fase'}</h2>
              <button type="button" className="btn-icon" onClick={() => setShowPhaseModal(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handlePhaseSubmit}>
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="form-group">
                  <label className="form-label">Nombre de la Fase</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    placeholder="Ej. Fase 1: Diseño, MVP..."
                    value={phaseForm.name}
                    onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label className="form-label">Fecha de Inicio</label>
                    <input
                      type="date"
                      className="form-input"
                      value={phaseForm.startDate}
                      onChange={(e) => setPhaseForm({ ...phaseForm, startDate: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label">Fecha Límite</label>
                    <input
                      type="date"
                      className="form-input"
                      value={phaseForm.dueDate}
                      onChange={(e) => setPhaseForm({ ...phaseForm, dueDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '16px 24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowPhaseModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPhase ? 'Guardar Cambios' : 'Crear Fase'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && (
        <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>{editingNote ? 'Edit Note' : 'Create Note'}</h2>
              <button type="button" className="btn-icon" onClick={() => setShowNoteModal(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleNoteSubmit}>
              <div className="modal-body" style={{ padding: '24px' }}>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    placeholder="Note title..."
                    value={noteForm.title}
                    onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Content</label>
                  <textarea
                    className="form-textarea"
                    required
                    placeholder="Write your note contents here..."
                    rows={6}
                    value={noteForm.content}
                    onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '16px 24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowNoteModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingNote ? 'Save Changes' : 'Save Note'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewAsset && (
        <div className="modal-overlay" onClick={() => setPreviewAsset(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90vw' }}>
            <div className="modal-header">
              <h2>Preview: {previewAsset.name}</h2>
              <button type="button" className="btn-icon" onClick={() => setPreviewAsset(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'var(--background)' }}>
              {previewAsset.fileType === 'image' ? (
                <img
                  src={previewAsset.url}
                  alt={previewAsset.name}
                  style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px', objectFit: 'contain', boxShadow: 'var(--shadow-md)' }}
                />
              ) : previewAsset.fileType === 'pdf' ? (
                <iframe
                  src={previewAsset.url}
                  width="100%"
                  height="600px"
                  style={{ border: 'none', borderRadius: '8px', boxShadow: 'var(--shadow-md)' }}
                  title={previewAsset.name}
                />
              ) : (
                <div style={{ padding: '40px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                  <div className={`asset-icon-wrapper ${previewAsset.fileType || 'other'}`} style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto' }}>
                    {getAssetIcon(previewAsset.fileType)}
                  </div>
                  <div>
                    <h4 style={{ fontWeight: 700, fontSize: '16px', marginBottom: '4px' }}>No preview available</h4>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Preview is not supported for this file type.</p>
                  </div>
                  <a
                    href={previewAsset.url}
                    download={previewAsset.name}
                    className="btn btn-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    <Download size={16} /> Download File
                  </a>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '16px 20px' }}>
              <a
                href={previewAsset.url}
                download={previewAsset.name}
                className="btn btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <Download size={14} /> Download
              </a>
              <button type="button" className="btn btn-primary" onClick={() => setPreviewAsset(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roadmap Preview Modal */}
      {showRoadmapPreview && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-container" style={{ width: '90%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', boxShadow: 'var(--shadow-lg)' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Eye size={18} /> Previsualización del Roadmap
              </h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowRoadmapPreview(false)}
                style={{ padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Esta es la previsualización del cronograma del proyecto. Puedes descargarlo como una imagen de alta resolución o exportarlo a PDF para impresión.
              </p>

              {/* High fidelity container replica */}
              <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '20px', background: '#ffffff', color: '#0f172a', overflowX: 'auto' }}>
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>{project?.name}</h4>
                  <span style={{ fontSize: '11px', color: '#475569' }}>Cronograma General de Fases y Tareas</span>
                </div>
                
                {/* Timeline Grid & Rows */}
                {(() => {
                  const sortedPhases = [...(project?.phases || [])].sort((a, b) => {
                    if (!a.startDate) return 1;
                    if (!b.startDate) return -1;
                    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
                  });

                  if (sortedPhases.length === 0) return null;

                  let timelineStart = project?.startDate ? new Date(project.startDate) : new Date();
                  let timelineEnd = project?.dueDate ? new Date(project.dueDate) : new Date();

                  sortedPhases.forEach(p => {
                    if (p.startDate) {
                      const d = new Date(p.startDate);
                      if (d < timelineStart) timelineStart = d;
                    }
                    if (p.dueDate) {
                      const d = new Date(p.dueDate);
                      if (d > timelineEnd) timelineEnd = d;
                    }
                  });

                  timelineStart = new Date(timelineStart.getTime() - 7 * 24 * 60 * 60 * 1000);
                  timelineEnd = new Date(timelineEnd.getTime() + 7 * 24 * 60 * 60 * 1000);

                  const totalDays = Math.ceil((timelineEnd.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));

                  const getPhaseTimelineProps = (phase: any) => {
                    if (!phase.startDate || !phase.dueDate) return null;
                    const pStart = new Date(phase.startDate);
                    const pEnd = new Date(phase.dueDate);
                    const offset = Math.max(0, Math.ceil((pStart.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
                    const duration = Math.max(1, Math.ceil((pEnd.getTime() - pStart.getTime()) / (1000 * 60 * 60 * 24)));
                    return {
                      left: (offset / totalDays) * 100,
                      width: (duration / totalDays) * 100
                    };
                  };

                  const getTaskTimelineProps = (phase: any, task: any) => {
                    if (!task.dueDate) return null;
                    const tEnd = new Date(task.dueDate);
                    const tStart = phase.startDate ? new Date(phase.startDate) : new Date(timelineStart);
                    const startVal = tStart < timelineStart ? timelineStart : tStart;
                    const endVal = tEnd > timelineEnd ? timelineEnd : (tEnd < startVal ? startVal : tEnd);
                    
                    const offset = Math.max(0, Math.ceil((startVal.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)));
                    const duration = Math.max(1, Math.ceil((endVal.getTime() - startVal.getTime()) / (1000 * 60 * 60 * 24)));
                    return {
                      left: (offset / totalDays) * 100,
                      width: (duration / totalDays) * 100
                    };
                  };

                  const getMonthMarkers = () => {
                    const markers = [];
                    const current = new Date(timelineStart);
                    while (current <= timelineEnd) {
                      markers.push(new Date(current));
                      current.setMonth(current.getMonth() + 1);
                    }
                    return markers;
                  };

                  const getTaskStatusColor = (status: string) => {
                    switch (status) {
                      case 'Todo': return '#64748b';
                      case 'In Progress': return '#f59e0b';
                      case 'Review': return '#8b5cf6';
                      case 'Done': return '#10b981';
                      default: return '#64748b';
                    }
                  };

                  const monthMarkers = getMonthMarkers();

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: '800px' }}>
                      <div style={{ display: 'flex', borderBottom: '1px solid #cbd5e1', paddingBottom: '8px' }}>
                        <div style={{ width: '320px', fontWeight: 'bold', fontSize: '12px', color: '#475569' }}>Fase / Tarea</div>
                        <div style={{ flex: 1, position: 'relative', height: '16px' }}>
                          {monthMarkers.map((marker, i) => {
                            const diff = marker.getTime() - timelineStart.getTime();
                            const left = (diff / (1000 * 60 * 60 * 24) / totalDays) * 100;
                            return (
                              <span key={i} style={{ position: 'absolute', left: `${left}%`, transform: 'translateX(-50%)', fontSize: '9px', color: '#64748b', fontWeight: 'bold' }}>
                                {marker.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' }).toUpperCase()}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative', marginTop: '10px' }}>
                        {sortedPhases.map((phase: any) => {
                          const props = getPhaseTimelineProps(phase);
                          const phaseTasks = project?.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
                          const total = phaseTasks.length;
                          const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
                          const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

                          return (
                            <React.Fragment key={phase.id}>
                              {/* Phase Bar */}
                              <div style={{ display: 'flex', alignItems: 'center', minHeight: '36px' }}>
                                <div style={{ width: '320px', paddingRight: '8px' }}>
                                  <div style={{ fontWeight: 'bold', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#0f172a' }}>
                                    {phase.name}
                                  </div>
                                  <div style={{ fontSize: '9px', color: '#64748b' }}>
                                    {formatCalendarDate(phase.startDate, false, 'es')} - {formatCalendarDate(phase.dueDate, false, 'es')}
                                  </div>
                                </div>
                                <div style={{ flex: 1, position: 'relative', height: '18px' }}>
                                  {props && (
                                    <div style={{ position: 'absolute', left: `${props.left}%`, width: `${props.width}%`, height: '100%', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                      {pct > 0 && <div style={{ height: '100%', width: `${pct}%`, background: '#4f46e5' }} />}
                                      <span style={{ position: 'absolute', left: '6px', fontSize: '9px', fontWeight: 'bold', color: pct > 45 ? '#ffffff' : '#0f172a' }}>{pct}%</span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Task Bars */}
                              {phaseTasks.map((task: any) => {
                                const tProps = getTaskTimelineProps(phase, task);
                                const assigneeInfo = getTaskAssigneeInfo(task);
                                return (
                                  <div key={task.id} style={{ display: 'flex', alignItems: 'center', minHeight: '28px', paddingLeft: '12px' }}>
                                    <div style={{ width: '308px', paddingRight: '8px', display: 'flex', flexDirection: 'column' }}>
                                      <div style={{ fontSize: '11px', fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        ↳ {task.name}
                                      </div>
                                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                        <span style={{ fontSize: '8px', padding: '2px 6px', borderRadius: '999px', background: assigneeInfo.isInput ? '#fef3c7' : '#e0e7ff', color: assigneeInfo.isInput ? '#92400e' : '#3730a3', border: assigneeInfo.isInput ? '1px solid #fde68a' : '1px solid #c7d2fe', fontWeight: 700 }}>
                                          {assigneeInfo.type}
                                        </span>
                                        <span style={{ fontSize: '8px', padding: '2px 6px', borderRadius: '999px', background: '#f8fafc', color: '#334155', border: '1px solid #cbd5e1', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {assigneeInfo.name}
                                        </span>
                                      </div>
                                    </div>
                                    <div style={{ flex: 1, position: 'relative', height: '10px' }}>
                                      {tProps && (
                                        <div style={{ position: 'absolute', left: `${tProps.left}%`, width: `${tProps.width}%`, height: '100%', background: getTaskStatusColor(task.status), borderRadius: '3px', opacity: 0.8 }} />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowRoadmapPreview(false)}
              >
                Cerrar
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  handleExportRoadmapImage();
                  setShowRoadmapPreview(false);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Download size={16} /> Descargar Imagen
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  handleExportRoadmapPDF();
                  setShowRoadmapPreview(false);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                Descargar PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* List Preview Modal */}
      {showListPreview && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-container" style={{ width: '90%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', boxShadow: 'var(--shadow-lg)' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Eye size={18} /> Previsualización de la Lista de Fases
              </h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowListPreview(false)}
                style={{ padding: '6px', borderRadius: '4px', cursor: 'pointer' }}
              >
                <X size={18} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
                Esta es la previsualización del reporte de lista detallada del proyecto. Puedes descargarlo como una imagen de alta resolución o exportarlo a PDF para impresión.
              </p>
              {/* High fidelity container replica (Light Mode) */}
              <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '20px', background: '#ffffff', color: '#0f172a', overflowX: 'auto' }}>
                <div style={{ marginBottom: '20px', borderBottom: '1px solid #cbd5e1', paddingBottom: '12px' }}>
                  <h4 style={{ margin: 0, fontSize: '18px', color: '#0f172a', fontWeight: 700 }}>{project?.name}</h4>
                  <span style={{ fontSize: '11px', color: '#475569' }}>Lista Detallada de Fases y Tareas</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '800px' }}>
                  {/* Table Header */}
                  <div style={{ display: 'flex', borderBottom: '1px solid #cbd5e1', paddingBottom: '8px', marginBottom: '10px' }}>
                    <div style={{ width: '350px', boxSizing: 'border-box', fontWeight: 'bold', fontSize: '12px', color: '#475569', paddingLeft: '10px' }}>FASE / TAREA</div>
                    <div style={{ width: '160px', boxSizing: 'border-box', fontWeight: 'bold', fontSize: '12px', color: '#475569' }}>FECHAS</div>
                    <div style={{ width: '140px', boxSizing: 'border-box', fontWeight: 'bold', fontSize: '12px', color: '#475569' }}>ESTADO</div>
                    <div style={{ flex: 1, fontWeight: 'bold', fontSize: '12px', color: '#475569' }}>RESPONSABLE</div>
                  </div>

                  {/* Rows */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(() => {
                      const sortedPhases = [...(project?.phases || [])].sort((a, b) => {
                        if (!a.startDate) return 1;
                        if (!b.startDate) return -1;
                        return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
                      });

                      const getTaskStatusColor = (status: string) => {
                        switch (status) {
                          case 'Todo': return '#64748b';
                          case 'In Progress': return '#f59e0b';
                          case 'Review': return '#8b5cf6';
                          case 'Done': return '#10b981';
                          default: return '#64748b';
                        }
                      };

                      return sortedPhases.map((phase: any) => {
                        const phaseTasks = project?.tasks?.filter((t: any) => t.phaseId === phase.id) || [];
                        const total = phaseTasks.length;
                        const completed = phaseTasks.filter((t: any) => t.status === 'Done').length;
                        const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

                        return (
                          <React.Fragment key={phase.id}>
                            {/* Phase Row */}
                            <div style={{ display: 'flex', alignItems: 'center', minHeight: '36px', background: '#f1f5f9', borderRadius: '4px', padding: '4px 0', border: '1px solid #cbd5e1' }}>
                              <div style={{ width: '350px', boxSizing: 'border-box', paddingLeft: '10px', fontWeight: 'bold', fontSize: '13px', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {phase.name}
                              </div>
                              <div style={{ width: '160px', boxSizing: 'border-box', fontSize: '11px', color: '#334155' }}>
                                {formatCalendarDate(phase.startDate, true, 'es')} - {formatCalendarDate(phase.dueDate, true, 'es')}
                              </div>
                              <div style={{ width: '140px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '11px', color: '#334155' }}>
                                  {completed} de {total} compl. ({pct}%)
                                </span>
                                <div style={{ width: '100px', height: '6px', background: '#cbd5e1', borderRadius: '3px', overflow: 'hidden' }}>
                                  <div style={{ width: `${pct}%`, height: '100%', background: '#3b82f6' }} />
                                </div>
                              </div>
                              <div style={{ flex: 1, fontSize: '11px', color: '#475569' }}>
                                N/A
                              </div>
                            </div>
                            
                            {/* Task Rows */}
                            {phaseTasks.map((task: any) => {
                              const assigneeInfo = getTaskAssigneeInfo(task);
                              return (
                                <div key={task.id} style={{ display: 'flex', alignItems: 'center', minHeight: '32px', borderBottom: '1px solid #e2e8f0' }}>
                                  <div style={{ width: '350px', boxSizing: 'border-box', paddingLeft: '20px', fontSize: '12px', color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    ↳ {task.name}
                                  </div>
                                  <div style={{ width: '160px', boxSizing: 'border-box', fontSize: '11px', color: '#64748b' }}>
                                    {task.dueDate ? formatCalendarDate(task.dueDate, false, 'es') : '-'}
                                  </div>
                                  <div style={{ width: '140px', boxSizing: 'border-box' }}>
                                    <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: getTaskStatusColor(task.status) + '20', color: getTaskStatusColor(task.status), fontWeight: 600 }}>
                                      {task.status === 'In Progress' ? 'En Progreso' : task.status === 'Todo' ? 'Pendiente' : task.status === 'Review' ? 'Revisión' : 'Completado'}
                                    </span>
                                  </div>
                                  <div style={{ flex: 1, display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0, overflow: 'hidden' }}>
                                    <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '999px', background: assigneeInfo.isInput ? '#fef3c7' : '#e0e7ff', color: assigneeInfo.isInput ? '#92400e' : '#3730a3', border: assigneeInfo.isInput ? '1px solid #fde68a' : '1px solid #c7d2fe', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                      {assigneeInfo.type}
                                    </span>
                                    <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '999px', background: '#f8fafc', color: '#334155', border: '1px solid #cbd5e1', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {assigneeInfo.name}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowListPreview(false)}
              >
                Cerrar
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  handleExportListImage();
                  setShowListPreview(false);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Download size={16} /> Descargar Imagen
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  handleExportListPDF();
                  setShowListPreview(false);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                Descargar PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ProjectDetail;
